import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Accordion,
  Collapse,
  Col,
  Form,
  Row,
  Card,
  Tooltip,
  OverlayTrigger,
  Tab,
  Nav,
  InputGroup,
  Modal,
} from "react-bootstrap";
import axios from "axios";
import { Button } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import DropzoneComponent from "../Dropzone.js";
import { domainConfig } from "../../config.js";
import NotyfContext from "../../contexts/NotyfContext.js";
import PopoverPicker from "../../components/PopoverPicker.js";
import { ShowConfirm } from "../../components/Alerts.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCircleCheck,
  faClose,
  faExclamationCircle,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import {
  redirectURL,
  HelpTooltip,
} from "../../assets/functions.js";
import { BackgroundFormComponent } from "../../components/Applications";


const cloneFalAiValue = (value) => {
  if (value === undefined) {
    return undefined;
  }

  return JSON.parse(JSON.stringify(value));
};

const FAL_AI_SUPPORTED_MODELS = [
  "fal-ai/nano-banana-pro/edit",
  "fal-ai/nano-banana/edit",
];
const FAL_AI_SUPPORTED_ASPECT_RATIOS = [
  "auto",
  "21:9",
  "16:9",
  "3:2",
  "4:3",
  "5:4",
  "1:1",
  "4:5",
  "3:4",
  "2:3",
  "9:16",
];
const FAL_AI_SUPPORTED_OUTPUT_FORMATS = ["png", "jpeg", "webp"];
const FAL_AI_SUPPORTED_RESOLUTIONS = ["1K", "2K", "4K"];

const createFalAiOptionId = () =>
  `falai-option-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

const getFalAiOptionId = (option = {}) =>
  typeof option?.id === "string" ? option.id.trim() : "";

const getFalAiMoreOptionPossibilityId = (possibility = {}) =>
  typeof possibility?.id === "string" ? possibility.id.trim() : "";

const getFalAiStepSizeValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return `${value}px`;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim();
    const matchedValue = normalizedValue.match(/^(\d+(?:\.\d+)?)\s*(px)?$/i);

    if (matchedValue && Number(matchedValue[1]) > 0) {
      return `${matchedValue[1]}px`;
    }
  }

  return "";
};

const getFalAiStepSizeNumberValue = (value) => {
  const normalizedValue = getFalAiStepSizeValue(value);

  return normalizedValue.replace(/px$/i, "");
};

const createFalAiStepOptionTemplate = () => ({
  id: createFalAiOptionId(),
  label: "",
  value: "",
  image: "",
  useAsReferenceImage: true,
});

const createFalAiStepTemplate = (id) => ({
  id,
  question: "",
  type: "select",
  required: true,
  autoAdvance: false,
  optionImageWidth: "220px",
  optionImageHeight: "220px",
  options: [createFalAiStepOptionTemplate()],
  moreOptions: [],
});

const createFalAiMoreOptionTemplate = (label = "campo1", stepOptions = []) => ({
  label,
  possibilities: (Array.isArray(stepOptions) ? stepOptions : []).map((option) => ({
    id: getFalAiOptionId(option),
    values: [""],
  })),
});

const getFalAiMoreOptionCacheKey = (stepId, label = "") =>
  `${stepId}::${label}`;

const createFalAiStepPlaceholderTemplate = (stepId = "") => `{${stepId}}`;

const createFalAiNestedPlaceholderTemplate = (stepId, optionLabel = "campo1") =>
  `{${stepId}.${optionLabel}}`;

const getFalAiMoreOptionLabelFromTokenInput = (stepId, tokenValue = "") => {
  const normalizedToken = tokenValue
    .trim()
    .replace(/^\{/, "")
    .replace(/\}$/, "");

  if (!normalizedToken) {
    return "";
  }

  const stepPrefix = `${stepId}.`;

  if (normalizedToken.startsWith(stepPrefix)) {
    return normalizedToken.slice(stepPrefix.length);
  }

  const nestedTokenParts = normalizedToken.split(".");

  if (nestedTokenParts.length > 1) {
    return nestedTokenParts.slice(1).join(".");
  }

  return normalizedToken;
};

const getNextFalAiMoreOptionLabel = (moreOptions = []) => {
  const existingLabels = new Set(
    (Array.isArray(moreOptions) ? moreOptions : [])
      .map((option) =>
        typeof option?.label === "string" ? option.label.trim() : "",
      )
      .filter(Boolean),
  );
  let nextIndex = 1;

  while (existingLabels.has(`campo${nextIndex}`)) {
    nextIndex += 1;
  }

  return `campo${nextIndex}`;
};

const replaceFalAiPromptToken = (prompt = "", token = "", value = "") => {
  if (!token) {
    return prompt;
  }

  return prompt.split(token).join(value);
};

const hasFalAiPromptContributionValue = (value) => {
  if (typeof value === "number") {
    return true;
  }

  if (typeof value === "string") {
    return true;
  }

  return value !== undefined && value !== null;
};

const hasFalAiPromptVisibleValue = (value) => {
  if (typeof value === "number") {
    return true;
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  return value !== undefined && value !== null;
};

const getFalAiPromptContributionText = (value, emptyFallback = "") => {
  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return value;
  }

  if (value === undefined || value === null) {
    return emptyFallback;
  }

  return String(value);
};

const isFalAiOptionStep = (step = {}) => step?.type !== "text";

const getFalAiStepOptionLabel = (option = {}, optionIdx = 0) =>
  option?.label || option?.value || `Opção ${optionIdx + 1}`;

const getFalAiNormalizedOptionValue = (value) => {
  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return value;
  }

  if (value === undefined || value === null) {
    return "";
  }

  return String(value);
};

const getFalAiMoreOptionPossibilityValues = (possibility = {}) => {
  const values = Array.isArray(possibility?.values) ? possibility.values : [];

  if (values.length === 0) {
    return [""];
  }

  return values.map((value) => getFalAiNormalizedOptionValue(value));
};

const normalizeFalAiStepOptions = (step = {}) => {
  if (!isFalAiOptionStep(step)) {
    return [];
  }

  const sourceOptions =
    Array.isArray(step?.options) && step.options.length > 0
      ? step.options
      : [createFalAiStepOptionTemplate()];

  return sourceOptions.map((option) => ({
    id: getFalAiOptionId(option) || createFalAiOptionId(),
    label: typeof option?.label === "string" ? option.label : "",
    value: getFalAiNormalizedOptionValue(option?.value),
    image: typeof option?.image === "string" ? option.image : "",
    useAsReferenceImage: option?.useAsReferenceImage !== false,
  }));
};

const normalizeFalAiMoreOptionPossibilities = (
  possibilities = [],
  stepOptions = [],
) => {
  const currentPossibilityMap = {};

  (Array.isArray(possibilities) ? possibilities : []).forEach((possibility) => {
    const possibilityId = getFalAiMoreOptionPossibilityId(possibility);

    if (!possibilityId || currentPossibilityMap[possibilityId]) {
      return;
    }

    currentPossibilityMap[possibilityId] = {
      id: possibilityId,
      values: getFalAiMoreOptionPossibilityValues(possibility),
    };
  });

  return (Array.isArray(stepOptions) ? stepOptions : []).map((option) => {
    const optionId = getFalAiOptionId(option);
    const currentPossibility = currentPossibilityMap[optionId];

    if (currentPossibility) {
      return currentPossibility;
    }

    return {
      id: optionId,
      values: [""],
    };
  });
};

const normalizeFalAiStepMoreOptionsConfig = (
  moreOptions = [],
  stepOptions = [],
) => {
  if ((Array.isArray(stepOptions) ? stepOptions : []).length === 0) {
    return [];
  }

  const currentMoreOptionMap = {};

  (Array.isArray(moreOptions) ? moreOptions : []).forEach((moreOption) => {
    const moreOptionLabel =
      typeof moreOption?.label === "string" ? moreOption.label.trim() : "";

    if (!moreOptionLabel || currentMoreOptionMap[moreOptionLabel]) {
      return;
    }

    currentMoreOptionMap[moreOptionLabel] = {
      label: moreOptionLabel,
      possibilities: normalizeFalAiMoreOptionPossibilities(
        moreOption?.possibilities,
        stepOptions,
      ),
    };
  });

  return Object.values(currentMoreOptionMap);
};

const normalizeFalAiStepConfig = (step = {}) => {
  const nextStep = cloneFalAiValue(step) || {};
  const nextOptions = normalizeFalAiStepOptions(nextStep);

  return {
    ...nextStep,
    required: nextStep?.type === "hidden" ? true : nextStep?.required !== false,
    autoAdvance: nextStep?.type === "select" ? nextStep?.autoAdvance === true : false,
    optionImageWidth:
      getFalAiStepSizeValue(nextStep?.optionImageWidth) || "220px",
    optionImageHeight:
      getFalAiStepSizeValue(nextStep?.optionImageHeight) || "220px",
    options: nextOptions,
    moreOptions: isFalAiOptionStep(nextStep)
      ? normalizeFalAiStepMoreOptionsConfig(nextStep?.moreOptions, nextOptions)
      : [],
  };
};

const getFalAiPreviewDefaultSelections = (falAiConfig = {}) => {
  const selections = {};
  const steps = Array.isArray(falAiConfig.prompt?.steps)
    ? falAiConfig.prompt.steps
    : [];

  steps.forEach((step) => {
    if (!step?.id) {
      return;
    }

    if (step.type === "text") {
      const sampleValue = step.placeholder?.trim();

      if (sampleValue) {
        selections[step.id] = sampleValue;
      }
      return;
    }

    const options = normalizeFalAiStepOptions(step);
    const defaultOption =
      options.find((option) => hasFalAiPromptVisibleValue(option.value)) ||
      options[0];

    if (defaultOption) {
      selections[step.id] = getFalAiOptionId(defaultOption);
    }
  });

  return selections;
};

const normalizeFalAiPreviewSelections = (
  falAiConfig = {},
  currentSelections = {},
) => {
  const defaults = getFalAiPreviewDefaultSelections(falAiConfig);
  const selections = {};
  const steps = Array.isArray(falAiConfig.prompt?.steps)
    ? falAiConfig.prompt.steps
    : [];

  steps.forEach((step) => {
    if (!step?.id) {
      return;
    }

    const hasCurrentSelection = Object.prototype.hasOwnProperty.call(
      currentSelections || {},
      step.id,
    );

    if (step.type === "text") {
      if (hasCurrentSelection) {
        selections[step.id] = String(currentSelections[step.id] || "");
      } else if (Object.prototype.hasOwnProperty.call(defaults, step.id)) {
        selections[step.id] = defaults[step.id];
      }
      return;
    }

    const options = normalizeFalAiStepOptions(step);
    const currentValue = hasCurrentSelection
      ? String(currentSelections[step.id] || "").trim()
      : "";
    const isOptionalEmptySelection =
      currentValue === "" && step.required === false;
    const hasValidOption = options.some(
      (option) => getFalAiOptionId(option) === currentValue,
    );

    if (isOptionalEmptySelection || hasValidOption) {
      selections[step.id] = currentValue;
    } else if (Object.prototype.hasOwnProperty.call(defaults, step.id)) {
      selections[step.id] = defaults[step.id];
    }
  });

  return selections;
};

const getFalAiPreviewStepStates = (falAiConfig = {}, selections = {}) => {
  const steps = Array.isArray(falAiConfig.prompt?.steps)
    ? falAiConfig.prompt.steps
    : [];
  const stepMap = new Map();
  const stateCache = new Map();
  const resolvingStepIds = new Set();

  steps.forEach((step) => {
    if (step?.id) {
      stepMap.set(step.id, step);
    }
  });

  const resolveStep = (stepId) => {
    if (stateCache.has(stepId)) {
      return stateCache.get(stepId);
    }

    const step = stepMap.get(stepId);
    const emptyState = {
      stepId,
      step,
      type: step?.type || "select",
      isActive: false,
      isSkipped: false,
      optionId: "",
      value: "",
      label: "",
      image: "",
    };

    if (!step || resolvingStepIds.has(stepId)) {
      stateCache.set(stepId, emptyState);
      return emptyState;
    }

    resolvingStepIds.add(stepId);
    let nextState = { ...emptyState, type: step.type };

    if (step.skipIf?.stepId) {
      const skipSourceStep = stepMap.get(step.skipIf.stepId);
      const skipSourceState = resolveStep(step.skipIf.stepId);
      const shouldSkip = skipSourceStep?.type === "text"
        ? skipSourceState.value === step.skipIf.value
        : skipSourceState.optionId === step.skipIf.value;

      if (shouldSkip) {
        nextState = { ...nextState, isSkipped: true };
      }
    }

    if (!nextState.isSkipped) {
      if (step.type === "text") {
        const value = Object.prototype.hasOwnProperty.call(selections, stepId)
          ? String(selections[stepId] || "")
          : "";

        nextState = {
          ...nextState,
          isActive: value.trim() !== "",
          value,
        };
      } else {
        const options = normalizeFalAiStepOptions(step);
        const selectedOption = options.find(
          (option) =>
            getFalAiOptionId(option) ===
            String(selections[stepId] || "").trim(),
        );

        nextState = {
          ...nextState,
          isActive: Boolean(selectedOption),
          optionId: getFalAiOptionId(selectedOption),
          value: getFalAiPromptContributionText(selectedOption?.value, ""),
          label: getFalAiPromptContributionText(selectedOption?.label, ""),
          image: selectedOption?.image || "",
        };
      }
    }

    resolvingStepIds.delete(stepId);
    stateCache.set(stepId, nextState);
    return nextState;
  };

  return steps.reduce((result, step) => {
    if (step?.id) {
      result[step.id] = resolveStep(step.id);
    }
    return result;
  }, {});
};

const resolveFalAiPreviewPrompt = (
  prompt = "",
  falAiConfig = {},
  selections = {},
) => {
  const steps = Array.isArray(falAiConfig.prompt?.steps)
    ? falAiConfig.prompt.steps
    : [];
  const stepMap = new Map(
    steps.filter((step) => step?.id).map((step) => [step.id, step]),
  );
  const stepStates = getFalAiPreviewStepStates(falAiConfig, selections);
  const invalidTokens = [];
  const resolvedTokens = {};

  const addInvalidToken = (token) => {
    if (token && !invalidTokens.includes(token)) {
      invalidTokens.push(token);
    }
  };

  const sanitizedPrompt = prompt.replace(/\[[^\[\]]+\]/g, (match) => {
    addInvalidToken(match);
    return "";
  });

  const resolvedPrompt = sanitizedPrompt.replace(
    /\{([^}]+)\}/g,
    (match, rawToken) => {
      const token = rawToken.trim();

      if (!token) {
        addInvalidToken(match);
        return "";
      }

      const nestedMatch = token.match(/^([^.{}\s]+)\.([^.{}\s]+)$/);

      if (nestedMatch) {
        const [, stepId, label] = nestedMatch;
        const step = stepMap.get(stepId);
        const moreOption = Array.isArray(step?.moreOptions)
          ? step.moreOptions.find((option) => option?.label === label)
          : null;
        const stepState = stepStates[stepId];

        if (!step || !moreOption) {
          addInvalidToken(match);
          return "";
        }

        if (!stepState?.isActive) {
          resolvedTokens[`${stepId}.${label}`] = "";
          return "";
        }

        const resolvedValue = getFalAiMoreOptionValuesForOptionId(
          moreOption,
          stepState.optionId,
        ).find((value) => hasFalAiPromptVisibleValue(value)) || "";

        if (resolvedValue === "") {
          addInvalidToken(match);
        }

        resolvedTokens[`${stepId}.${label}`] = resolvedValue;
        return resolvedValue;
      }

      if (/^[^.{}\s]+$/.test(token)) {
        const step = stepMap.get(token);
        const stepState = stepStates[token];

        if (!step) {
          addInvalidToken(match);
          return "";
        }

        if (!stepState?.isActive) {
          resolvedTokens[token] = "";
          return "";
        }

        resolvedTokens[token] = stepState.value;
        return stepState.value;
      }

      addInvalidToken(match);
      return "";
    },
  );

  return { prompt: resolvedPrompt, invalidTokens, resolvedTokens, stepStates };
};

const getFalAiPreviewOptionControls = (falAiConfig = {}, selections = {}) => {
  const steps = Array.isArray(falAiConfig.prompt?.steps)
    ? falAiConfig.prompt.steps
    : [];
  const stepStates = getFalAiPreviewStepStates(falAiConfig, selections);

  return steps.reduce((controls, step) => {
    if (!step?.id || step.type === "text" || step.type === "hidden") {
      return controls;
    }

    const options = normalizeFalAiStepOptions(step);

    if (options.length === 0) {
      return controls;
    }

    const state = stepStates[step.id] || {};
    controls.push({
      id: step.id,
      label: step.id,
      options,
      selectedValue: state.optionId || "",
      isSkipped: state.isSkipped === true,
      required: step.required !== false,
    });
    return controls;
  }, []);
};

const getFalAiOptimizedItem = (items = [], id, idField = "id") => {
  if (!Array.isArray(items)) {
    return null;
  }

  return (
    items.find(
      (item) =>
        item &&
        typeof item === "object" &&
        item?.[idField] === id,
    ) || null
  );
};

const getFalAiOptimizedString = (value, fallback = "", allowEmpty = false) => {
  if (typeof value !== "string") {
    return fallback;
  }

  if (!allowEmpty && value.trim() === "") {
    return fallback;
  }

  return value;
};

const getFalAiOptimizedValues = (value, fallback = []) => {
  if (!Array.isArray(value)) {
    return fallback;
  }

  const nextValues = value
    .filter((item) => typeof item === "string" || typeof item === "number")
    .map((item) => String(item))
    .filter((item) => item.trim() !== "");

  return nextValues.length > 0 ? nextValues : fallback;
};

const getFalAiPromptTokenSignature = (prompt = "") => {
  if (typeof prompt !== "string" || prompt.trim() === "") {
    return null;
  }

  const tokens = [];
  let hasInvalidToken = false;
  const promptWithoutTokens = prompt.replace(
    /\{([^}]*)\}/g,
    (match, rawToken) => {
      const token = rawToken.trim();

      if (
        !/^[^.{}\s]+$/.test(token) &&
        !/^([^.{}\s]+)\.([^.{}\s]+)$/.test(token)
      ) {
        hasInvalidToken = true;
      } else {
        tokens.push(token);
      }

      return "";
    },
  );

  if (
    hasInvalidToken ||
    /[{}]/.test(promptWithoutTokens) ||
    promptWithoutTokens.includes("[") ||
    promptWithoutTokens.includes("]") ||
    /\[[^[\]]+\]/.test(prompt)
  ) {
    return null;
  }

  return tokens.sort();
};

const hasSameFalAiItems = (currentItems = [], optimizedItems = []) => {
  if (
    !Array.isArray(currentItems) ||
    !Array.isArray(optimizedItems) ||
    currentItems.length !== optimizedItems.length
  ) {
    return false;
  }

  const currentSet = new Set(currentItems);
  const optimizedSet = new Set(optimizedItems);

  return (
    currentSet.size === currentItems.length &&
    optimizedSet.size === optimizedItems.length &&
    currentItems.every((item) => optimizedSet.has(item))
  );
};

const getFalAiOptimizedConfigError = (
  currentConfig = {},
  optimizedConfig = {},
) => {
  if (!optimizedConfig || typeof optimizedConfig !== "object") {
    return "A IA não retornou uma configuração otimizada completa.";
  }

  const currentPrompt =
    currentConfig?.prompt && typeof currentConfig.prompt === "object"
      ? currentConfig.prompt
      : {};
  const optimizedPrompt =
    optimizedConfig?.prompt && typeof optimizedConfig.prompt === "object"
      ? optimizedConfig.prompt
      : null;

  if (!optimizedPrompt) {
    return "A IA não retornou o bloco de prompt otimizado.";
  }

  if (
    !FAL_AI_SUPPORTED_MODELS.includes(optimizedConfig.model) ||
    !Array.isArray(optimizedPrompt.steps) ||
    !Array.isArray(optimizedConfig.extraReferences)
  ) {
    return "A IA retornou uma configuração otimizada incompleta.";
  }

  const optimizedGeneration = optimizedConfig.generation;
  const optimizedNumImages = Number(optimizedGeneration?.num_images);
  if (
    !optimizedGeneration ||
    typeof optimizedGeneration !== "object" ||
    !FAL_AI_SUPPORTED_ASPECT_RATIOS.includes(
      optimizedGeneration.aspect_ratio,
    ) ||
    !FAL_AI_SUPPORTED_OUTPUT_FORMATS.includes(
      optimizedGeneration.output_format,
    ) ||
    (optimizedConfig.model === "fal-ai/nano-banana-pro/edit" &&
      !FAL_AI_SUPPORTED_RESOLUTIONS.includes(
        optimizedGeneration.resolution,
      )) ||
    !Number.isFinite(optimizedNumImages) ||
    optimizedNumImages < 1 ||
    optimizedNumImages > 4 ||
    typeof optimizedGeneration.limit_generations !== "boolean" ||
    typeof optimizedGeneration.enable_web_search !== "boolean"
  ) {
    return "A IA retornou parâmetros de geração incompletos.";
  }

  const currentTokenSignature = getFalAiPromptTokenSignature(
    currentPrompt.base,
  );
  const optimizedTokenSignature = getFalAiPromptTokenSignature(
    optimizedPrompt.base,
  );

  if (
    !currentTokenSignature ||
    !optimizedTokenSignature ||
    JSON.stringify(currentTokenSignature) !==
    JSON.stringify(optimizedTokenSignature)
  ) {
    return "A IA alterou os placeholders do Prompt.";
  }

  const currentSteps = Array.isArray(currentPrompt.steps)
    ? currentPrompt.steps
    : [];
  const optimizedSteps = Array.isArray(optimizedPrompt.steps)
    ? optimizedPrompt.steps
    : [];

  if (
    !hasSameFalAiItems(
      currentSteps.map((step) => step.id),
      optimizedSteps.map((step) => step?.id),
    )
  ) {
    return "A IA alterou a estrutura das etapas.";
  }

  for (const currentStep of currentSteps) {
    const optimizedStep = getFalAiOptimizedItem(
      optimizedSteps,
      currentStep.id,
    );

    if (
      typeof optimizedStep?.question !== "string" ||
      typeof optimizedStep?.placeholder !== "string" ||
      (currentStep.type === "text" &&
        optimizedStep?.maxLength !== null &&
        !Number.isFinite(Number(optimizedStep?.maxLength)))
    ) {
      return `A IA retornou a etapa ${currentStep.id} incompleta.`;
    }

    const currentOptions = Array.isArray(currentStep.options)
      ? currentStep.options
      : [];
    const optimizedOptions = Array.isArray(optimizedStep?.options)
      ? optimizedStep.options
      : [];

    if (
      !hasSameFalAiItems(
        currentOptions.map((option) => option.id),
        optimizedOptions.map((option) => option?.id),
      )
    ) {
      return `A IA alterou as opções da etapa ${currentStep.id}.`;
    }

    for (const currentOption of currentOptions) {
      const optimizedOption = getFalAiOptimizedItem(
        optimizedOptions,
        currentOption.id,
      );

      if (
        typeof optimizedOption?.label !== "string" ||
        typeof optimizedOption?.value !== "string"
      ) {
        return `A IA retornou uma opção incompleta na etapa ${currentStep.id}.`;
      }
    }

    const currentMoreOptions = Array.isArray(currentStep.moreOptions)
      ? currentStep.moreOptions
      : [];
    const optimizedMoreOptions = Array.isArray(optimizedStep?.moreOptions)
      ? optimizedStep.moreOptions
      : [];

    if (
      !hasSameFalAiItems(
        currentMoreOptions.map((moreOption) => moreOption.label),
        optimizedMoreOptions.map((moreOption) => moreOption?.label),
      )
    ) {
      return `A IA alterou os elementos adicionais da etapa ${currentStep.id}.`;
    }

    for (const currentMoreOption of currentMoreOptions) {
      const optimizedMoreOption = getFalAiOptimizedItem(
        optimizedMoreOptions,
        currentMoreOption.label,
        "label",
      );
      const currentPossibilities = Array.isArray(
        currentMoreOption.possibilities,
      )
        ? currentMoreOption.possibilities
        : [];
      const optimizedPossibilities = Array.isArray(
        optimizedMoreOption?.possibilities,
      )
        ? optimizedMoreOption.possibilities
        : [];

      if (
        !hasSameFalAiItems(
          currentPossibilities.map((possibility) => possibility.id),
          optimizedPossibilities.map((possibility) => possibility?.id),
        )
      ) {
        return `A IA alterou as possibilidades de ${currentStep.id}.${currentMoreOption.label}.`;
      }

      for (const currentPossibility of currentPossibilities) {
        const optimizedPossibility = getFalAiOptimizedItem(
          optimizedPossibilities,
          currentPossibility.id,
        );

        if (
          !Array.isArray(optimizedPossibility?.values) ||
          optimizedPossibility.values.length === 0 ||
          optimizedPossibility.values.some(
            (value) =>
              typeof value !== "string" && typeof value !== "number",
          )
        ) {
          return `A IA retornou valores incompletos para ${currentStep.id}.${currentMoreOption.label}.`;
        }
      }
    }
  }

  const currentReferences = Array.isArray(currentConfig.extraReferences)
    ? currentConfig.extraReferences
    : [];
  const optimizedReferences = Array.isArray(optimizedConfig.extraReferences)
    ? optimizedConfig.extraReferences
    : [];

  if (
    !hasSameFalAiItems(
      currentReferences.map((reference, index) => index + 1),
      optimizedReferences.map((reference) => Number(reference?.order)),
    )
  ) {
    return "A IA alterou a estrutura das imagens de referência.";
  }

  if (
    optimizedReferences.some(
      (reference) => typeof reference?.hasImageReference !== "boolean",
    )
  ) {
    return "A IA retornou imagens de referência incompletas.";
  }

  return "";
};

const buildFalAiConfigChangeSummary = (
  currentConfig = {},
  nextConfig = {},
) => {
  const changes = [];
  const addChange = (label, currentValue, nextValue) => {
    if (JSON.stringify(currentValue) !== JSON.stringify(nextValue)) {
      changes.push(label);
    }
  };

  addChange(
    "Prompt",
    currentConfig?.prompt?.base,
    nextConfig?.prompt?.base,
  );
  addChange(
    "textos e valores das etapas",
    currentConfig?.prompt?.steps,
    nextConfig?.prompt?.steps,
  );
  addChange("modelo", currentConfig?.model, nextConfig?.model);
  addChange(
    "proporção da imagem",
    currentConfig?.aspect_ratio,
    nextConfig?.aspect_ratio,
  );
  addChange(
    "formato de saída",
    currentConfig?.output_format,
    nextConfig?.output_format,
  );
  addChange("resolução", currentConfig?.resolution, nextConfig?.resolution);
  addChange(
    "quantidade de imagens",
    currentConfig?.num_images,
    nextConfig?.num_images,
  );
  addChange(
    "limite de gerações",
    currentConfig?.limit_generations,
    nextConfig?.limit_generations,
  );
  addChange(
    "busca na web",
    currentConfig?.enable_web_search,
    nextConfig?.enable_web_search,
  );

  return changes;
};

const buildAppliedFalAiConfig = (
  currentConfig = {},
  optimizedConfig = {},
  rewrittenPrompt = "",
) => {
  const currentPrompt =
    currentConfig?.prompt && typeof currentConfig.prompt === "object"
      ? currentConfig.prompt
      : {};
  const optimizedPrompt =
    optimizedConfig?.prompt && typeof optimizedConfig.prompt === "object"
      ? optimizedConfig.prompt
      : {};
  const currentSteps = Array.isArray(currentPrompt.steps)
    ? currentPrompt.steps
    : [];
  const optimizedSteps = Array.isArray(optimizedPrompt.steps)
    ? optimizedPrompt.steps
    : [];
  const promptCandidate = getFalAiOptimizedString(
    optimizedPrompt.base,
    rewrittenPrompt,
  );
  const currentTokenSignature = getFalAiPromptTokenSignature(
    currentPrompt.base,
  );
  const optimizedTokenSignature = getFalAiPromptTokenSignature(
    promptCandidate,
  );
  const nextPromptBase =
    currentTokenSignature &&
      optimizedTokenSignature &&
      JSON.stringify(currentTokenSignature) ===
      JSON.stringify(optimizedTokenSignature)
      ? promptCandidate
      : currentPrompt.base || "";
  const nextSteps = currentSteps.map((currentStep) => {
    const normalizedStep = normalizeFalAiStepConfig(currentStep);
    const optimizedStep =
      getFalAiOptimizedItem(optimizedSteps, normalizedStep.id) || {};
    const optimizedOptions = Array.isArray(optimizedStep?.options)
      ? optimizedStep.options
      : [];
    const optimizedMoreOptions = Array.isArray(optimizedStep?.moreOptions)
      ? optimizedStep.moreOptions
      : [];
    const optimizedMaxLength =
      optimizedStep?.maxLength === null ||
        optimizedStep?.maxLength === undefined ||
        optimizedStep?.maxLength === ""
        ? NaN
        : Number(optimizedStep.maxLength);

    return normalizeFalAiStepConfig({
      ...normalizedStep,
      question: getFalAiOptimizedString(
        optimizedStep?.question,
        normalizedStep.question,
        normalizedStep.type === "hidden",
      ),
      placeholder: getFalAiOptimizedString(
        optimizedStep?.placeholder,
        normalizedStep.placeholder,
        true,
      ),
      maxLength:
        normalizedStep.type === "text" &&
          Number.isFinite(optimizedMaxLength)
          ? Math.max(1, Math.min(500, Math.round(optimizedMaxLength)))
          : normalizedStep.maxLength,
      options: normalizedStep.options.map((currentOption) => {
        const optimizedOption = getFalAiOptimizedItem(
          optimizedOptions,
          currentOption.id,
        ) || {};

        return {
          ...currentOption,
          label: getFalAiOptimizedString(
            optimizedOption?.label,
            currentOption.label,
            normalizedStep.type === "hidden",
          ),
          value: getFalAiOptimizedString(
            optimizedOption?.value,
            currentOption.value,
          ),
        };
      }),
      moreOptions: normalizedStep.moreOptions.map(
        (currentMoreOption) => {
          const optimizedMoreOption =
            getFalAiOptimizedItem(
              optimizedMoreOptions,
              currentMoreOption.label,
              "label",
            ) || {};
          const optimizedPossibilities = Array.isArray(
            optimizedMoreOption?.possibilities,
          )
            ? optimizedMoreOption.possibilities
            : [];

          return {
            ...currentMoreOption,
            possibilities: currentMoreOption.possibilities.map(
              (currentPossibility) => {
                const optimizedPossibility =
                  getFalAiOptimizedItem(
                    optimizedPossibilities,
                    currentPossibility.id,
                  ) || {};

                return {
                  ...currentPossibility,
                  values: getFalAiOptimizedValues(
                    optimizedPossibility?.values,
                    currentPossibility.values,
                  ),
                };
              },
            ),
          };
        },
      ),
    });
  });
  const nextExtraReferences = Array.isArray(currentConfig?.extraReferences)
    ? currentConfig.extraReferences
    : [];
  const optimizedGeneration =
    optimizedConfig?.generation &&
      typeof optimizedConfig.generation === "object"
      ? optimizedConfig.generation
      : {};
  const optimizedNumImages =
    optimizedGeneration.num_images === null ||
      optimizedGeneration.num_images === undefined ||
      optimizedGeneration.num_images === ""
      ? NaN
      : Number(optimizedGeneration.num_images);
  const nextModel = FAL_AI_SUPPORTED_MODELS.includes(optimizedConfig?.model)
    ? optimizedConfig.model
    : currentConfig.model;

  return {
    ...currentConfig,
    model: nextModel,
    prompt: {
      ...currentPrompt,
      base: nextPromptBase,
      steps: nextSteps,
    },
    extraReferences: nextExtraReferences,
    aspect_ratio: FAL_AI_SUPPORTED_ASPECT_RATIOS.includes(
      optimizedGeneration.aspect_ratio,
    )
      ? optimizedGeneration.aspect_ratio
      : currentConfig.aspect_ratio,
    output_format: FAL_AI_SUPPORTED_OUTPUT_FORMATS.includes(
      optimizedGeneration.output_format,
    )
      ? optimizedGeneration.output_format
      : currentConfig.output_format,
    resolution:
      nextModel === "fal-ai/nano-banana-pro/edit" &&
        FAL_AI_SUPPORTED_RESOLUTIONS.includes(
          optimizedGeneration.resolution,
        )
        ? optimizedGeneration.resolution
        : currentConfig.resolution,
    num_images: Number.isFinite(optimizedNumImages)
      ? Math.max(1, Math.min(4, Math.round(optimizedNumImages)))
      : currentConfig.num_images,
    limit_generations:
      typeof optimizedGeneration.limit_generations === "boolean"
        ? optimizedGeneration.limit_generations
        : currentConfig.limit_generations,
    enable_web_search:
      nextModel === "fal-ai/nano-banana-pro/edit" &&
        typeof optimizedGeneration.enable_web_search === "boolean"
        ? optimizedGeneration.enable_web_search
        : false,
  };
};

const getFalAiMoreOptionValuesForOptionId = (moreOption = {}, optionId = "") => {
  const possibility = (Array.isArray(moreOption?.possibilities)
    ? moreOption.possibilities
    : []
  ).find(
    (item) => getFalAiMoreOptionPossibilityId(item) === optionId,
  );

  return getFalAiMoreOptionPossibilityValues(possibility).filter((value) =>
    hasFalAiPromptVisibleValue(value),
  );
};

const hasFalAiImageReference = (value) => {
  if (typeof value === "string") {
    return value.trim() !== "";
  }

  return value !== undefined && value !== null;
};

const getFalAiImageUsageSummary = (falAiConfig = {}) => {
  const steps = Array.isArray(falAiConfig.prompt?.steps)
    ? falAiConfig.prompt.steps
    : [];
  const extraReferences = Array.isArray(falAiConfig.extraReferences)
    ? falAiConfig.extraReferences
    : [];
  const extraImageCount = extraReferences.filter((reference) =>
    hasFalAiImageReference(reference?.image),
  ).length;
  const stepImageCount = steps.filter(
    (step) =>
      step?.id &&
      step?.type !== "text" &&
      Array.isArray(step.options) &&
      step.options.some((option) => hasFalAiImageReference(option?.image)),
  ).length;
  const otherSourceImageCount = extraImageCount + stepImageCount;
  const totalImageCount = 1 + otherSourceImageCount;

  return {
    extraImageCount,
    stepImageCount,
    otherSourceImageCount,
    totalImageCount,
    truncatedImageCount: Math.max(0, totalImageCount - 14),
  };
};

const isFalAiPromptEmptyOrGeneric = (prompt = "") => {
  const trimmedPrompt = String(prompt || "").trim();

  if (!trimmedPrompt) {
    return true;
  }

  const promptWithoutTokens = trimmedPrompt
    .replace(/\{[^}]*\}/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const wordCount = promptWithoutTokens
    .split(/\s+/)
    .filter(Boolean).length;

  return trimmedPrompt.length < 24 || wordCount < 4;
};

const buildFalAiOperationalWarnings = (falAiConfig = {}) => {
  const warnings = [];
  const generatedImagesCount = Number(falAiConfig.num_images || 1);
  const isProModel = falAiConfig.model === "fal-ai/nano-banana-pro/edit";
  const uses4K = isProModel && falAiConfig.resolution === "4K";
  const imageUsage = getFalAiImageUsageSummary(falAiConfig);

  if (uses4K && generatedImagesCount > 1) {
    warnings.push(
      "Esta configuração combina 4K com múltiplas imagens geradas, aumentando bastante o custo operacional.",
    );
  } else {
    if (generatedImagesCount > 1) {
      warnings.push(
        "O prompt gerará mais de uma imagem, o que implica em maior custo operacional.",
      );
    }

    if (uses4K) {
      warnings.push(
        "O prompt irá gerar imagens em 4K, o que implica em maior custo operacional.",
      );
    }
  }

  if (isProModel && falAiConfig.enable_web_search === true) {
    warnings.push(
      "A busca na web pode aumentar o tempo de geração e alterar a previsibilidade do resultado.",
    );
  }

  if (imageUsage.truncatedImageCount > 0) {
    warnings.push(
      "Algumas imagens podem ser ignoradas pelo limite de envio do fal.ai. Revise a ordem das imagens antes de continuar.",
    );
  } else if (imageUsage.totalImageCount >= 12) {
    warnings.push(
      "Este prompt está próximo do limite de imagens enviadas ao fal.ai. Revise a ordem das imagens antes de continuar.",
    );
  } else if (imageUsage.otherSourceImageCount >= 6) {
    warnings.push(
      "Este prompt enviará várias imagens de referência ao modelo, o que pode aumentar o custo e deixar o resultado menos previsível.",
    );
  }

  if (isFalAiPromptEmptyOrGeneric(falAiConfig.prompt?.base)) {
    warnings.push(
      "O prompt está vazio ou muito curto. O resultado pode ficar genérico.",
    );
  }

  return warnings;
};

const getFalAiPromptBaseFeedbackMessage = ({
  invalidTokens = [],
  schemaError = "",
}) => {
  if (schemaError) {
    return schemaError;
  }

  if (invalidTokens.length > 0) {
    return `Use apenas placeholders no formato {variavel} e {variavel.campo1}: ${invalidTokens.join(", ")}`;
  }

  return "";
};

const buildFalAiPromptPreview = (
  falAiConfig = {},
  promptReferences = {},
  previewSelections = {},
) => {
  const promptBase = falAiConfig.prompt?.base || "";
  const trimmedPromptBase = promptBase.trim();
  const runtimeBasePrompt =
    trimmedPromptBase || "Generate an image based on the reference";
  const steps = Array.isArray(falAiConfig.prompt?.steps)
    ? falAiConfig.prompt.steps
    : [];
  const extraReferences = Array.isArray(falAiConfig.extraReferences)
    ? falAiConfig.extraReferences
    : [];
  const invalidTokens = Array.isArray(promptReferences?.invalidTokens)
    ? promptReferences.invalidTokens
    : [];
  const hasPromptBaseError = Boolean(promptReferences?.hasError);
  const previewStepStates = getFalAiPreviewStepStates(
    falAiConfig,
    previewSelections,
  );

  const replacementLines = [];
  const behaviorNotes = [];

  steps.forEach((step, stepIdx) => {
    if (!step?.id) {
      return;
    }

    const token = createFalAiStepPlaceholderTemplate(step.id);
    const availableOptions = normalizeFalAiStepOptions(step);
    const previewStepState = previewStepStates[step.id] || {};
    const nestedOptions = Array.isArray(step.moreOptions)
      ? normalizeFalAiStepMoreOptionsConfig(step.moreOptions, availableOptions)
      : [];
    const skipSourceSteps = steps.filter(
      (candidate, candidateIdx) =>
        candidateIdx < stepIdx && candidate?.type !== "hidden",
    );
    const skipSourceStep = skipSourceSteps.find(
      (candidate) => candidate.id === step?.skipIf?.stepId,
    );
    const skipSourceLabel =
      skipSourceStep?.type === "text"
        ? step?.skipIf?.value
        : (skipSourceStep?.options || []).find(
          (option) => getFalAiOptionId(option) === step?.skipIf?.value,
        )?.label || step?.skipIf?.value;
    const canSkipStep =
      (step?.required !== false || step?.type === "hidden") &&
      Boolean(step?.skipIf?.stepId) &&
      Boolean(skipSourceStep) &&
      hasFalAiPromptContributionValue(step?.skipIf?.value);

    if (step.type === "text") {
      const sampleValue =
        previewStepState.value ||
        step.placeholder?.trim() ||
        `texto informado em ${token}`;

      replacementLines.push(
        `${token} -> texto livre do usuário. Exemplo: ${sampleValue}`,
      );

      if (step.required === false) {
        behaviorNotes.push(
          `${token} é opcional. Se o usuário avançar sem responder, esse token fica vazio no prompt final.`,
        );
      }

      if (canSkipStep) {
        behaviorNotes.push(
          `${token} vira vazio quando ${step.skipIf.stepId} = ${skipSourceLabel}.`,
        );
      }
    } else {
      replacementLines.push(
        `${token} -> ${availableOptions.length > 0
          ? availableOptions
            .map((option, optionIdx) => {
              const optionLabel = getFalAiStepOptionLabel(option, optionIdx);
              const contribution = getFalAiPromptContributionText(
                option?.value,
                "",
              );

              return `${optionLabel}: ${contribution === "" ? "(remove do prompt)" : contribution
                }`;
            })
            .join(" | ")
          : "(sem opções configuradas)"}`,
      );

      if (step.required === false) {
        behaviorNotes.push(
          `${token} é opcional. Se o usuário não escolher nada, esse token fica vazio no prompt final.`,
        );
      }

      if (availableOptions.some((option) => getFalAiPromptContributionText(option?.value, "") === "")) {
        behaviorNotes.push(
          `${token} possui pelo menos uma opção com valor vazio, que remove esse trecho do prompt quando escolhida.`,
        );
      }

      if (step.type === "hidden") {
        behaviorNotes.push(
          `${token} sorteia uma das opções da etapa oculta ${step.id} no servidor antes de montar o prompt.`,
        );
      }

      if (canSkipStep) {
        behaviorNotes.push(
          `${token} vira vazio quando ${step.skipIf.stepId} = ${skipSourceLabel}.`,
        );
      }
    }

    nestedOptions.forEach((option) => {
      if (!option?.label) {
        return;
      }

      const optionToken = createFalAiNestedPlaceholderTemplate(
        step.id,
        option.label,
      );

      const hasAnyOptionValues = availableOptions.some(
        (stepOption) =>
          getFalAiMoreOptionValuesForOptionId(option, getFalAiOptionId(stepOption)).length > 0,
      );
      replacementLines.push(
        `${optionToken} -> ${hasAnyOptionValues
          ? availableOptions
            .map((stepOption, optionIdx) => {
              const groupedValues = getFalAiMoreOptionValuesForOptionId(
                option,
                getFalAiOptionId(stepOption),
              );

              return `${getFalAiStepOptionLabel(stepOption, optionIdx)}: ${groupedValues.length > 0
                ? groupedValues.join(" | ")
                : "(sem possibilidades configuradas)"
                }`;
            })
            .join(" | ")
          : "(sem possibilidades configuradas)"
        }`,
      );
    });

    if (nestedOptions.length > 0) {
      const exampleNestedToken = createFalAiNestedPlaceholderTemplate(
        step.id,
        nestedOptions[0]?.label || "campo1",
      );

      if (step.type === "hidden") {
        behaviorNotes.push(
          `Tokens adicionais como ${exampleNestedToken} usam a mesma opção sorteada para ${token}.`,
        );
      } else if (step.required === false) {
        behaviorNotes.push(
          `Tokens adicionais como ${exampleNestedToken} só entram quando a etapa ${step.id} tiver resposta. Sem resposta, eles viram vazio.`,
        );
      }

      if (canSkipStep) {
        behaviorNotes.push(
          `Tokens adicionais como ${exampleNestedToken} também viram vazio quando ${step.skipIf.stepId} = ${skipSourceLabel}.`,
        );
      }
    }
  });

  const configuredPrompt = trimmedPromptBase;
  const resolvedPreviewPrompt = resolveFalAiPreviewPrompt(
    runtimeBasePrompt,
    falAiConfig,
    previewSelections,
  );
  const previewInvalidTokens = Array.from(
    new Set([...invalidTokens, ...resolvedPreviewPrompt.invalidTokens]),
  );
  const previewOutput = previewInvalidTokens.length > 0
    ? "(corrija o Prompt para gerar a simulação)"
    : resolvedPreviewPrompt.prompt.trim();
  const extraImageCount = extraReferences.filter((reference) =>
    hasFalAiImageReference(reference?.image),
  ).length;
  const stepImageCount = steps.filter(
    (step) => {
      const previewStepState = previewStepStates[step?.id] || {};

      return (
        step?.type !== "text" &&
        previewStepState.isActive === true &&
        hasFalAiImageReference(previewStepState.image)
      );
    },
  ).length;
  const otherSourceImageCount = extraImageCount + stepImageCount;
  const cappedOtherSourceImageCount = Math.min(13, otherSourceImageCount);
  const maxImageCount = 1 + cappedOtherSourceImageCount;
  const truncatedImageCount = Math.max(0, otherSourceImageCount - 13);
  const sections = [];

  if (invalidTokens.length > 0) {
    sections.push(
      "ATENÇÃO\nCorrija o Prompt para liberar a simulação completa.",
    );
  } else if (hasPromptBaseError) {
    sections.push(
      "ATENÇÃO\nO Prompt possui erros de validação. Corrija-os para salvar.",
    );
  }

  if (!trimmedPromptBase) {
    behaviorNotes.push(
      'Se o Prompt ficar vazio, o servidor usa "Generate an image based on the reference" como fallback.',
    );
  }

  sections.push(`PROMPT CONFIGURADO\n${configuredPrompt || "(vazio)"}`);
  sections.push(`EXEMPLO DE SAÍDA\n${previewOutput || "(vazio)"}`);
  sections.push(
    `COMO CADA TOKEN PODE ENTRAR\n${replacementLines.length > 0
      ? replacementLines.map((line) => `- ${line}`).join("\n")
      : "- Nenhum placeholder configurado."
    }`,
  );

  const uniqueBehaviorNotes = Array.from(
    new Set(behaviorNotes.filter(Boolean)),
  );

  if (uniqueBehaviorNotes.length > 0) {
    sections.push(
      `REGRAS IMPORTANTES\n${uniqueBehaviorNotes
        .map((line) => `- ${line}`)
        .join("\n")}`,
    );
  }

  sections.push(
    `REFERÊNCIAS DE IMAGEM\n- 1 foto do usuário (sempre presente)\n- ${extraImageCount} imagem(ns) de referência\n- até ${stepImageCount} escolhas do fluxo com imagem e valor preenchido\n- outras fontes: ${cappedOtherSourceImageCount}/13 (limite = 14 total - 1 foto do usuário)\n- total máximo enviado ao fal.ai: ${maxImageCount}/14${truncatedImageCount > 0
      ? `\n- ${truncatedImageCount} referência(s) excedente(s) seriam descartadas pelo limite da API`
      : ""
    }`,
  );

  return sections.join("\n\n");
};

const extractFalAiPreviewSection = (previewText = "", sectionTitle = "") => {
  const sections = String(previewText || "").split(/\n\n+/);
  const section = sections.find((item) => item.startsWith(`${sectionTitle}\n`));

  return section ? section.slice(sectionTitle.length).trim() : "";
};

const buildFalAiPromptPreviewCards = (
  falAiConfig = {},
  promptReferences = {},
  previewSelections = {},
) => {
  const legacyPreview = buildFalAiPromptPreview(
    falAiConfig,
    promptReferences,
    previewSelections,
  );
  const configuredPrompt =
    extractFalAiPreviewSection(legacyPreview, "PROMPT CONFIGURADO") || "(vazio)";
  const previewOutput =
    extractFalAiPreviewSection(legacyPreview, "EXEMPLO DE SAÍDA") || "(vazio)";
  const legacyRulesSection =
    extractFalAiPreviewSection(legacyPreview, "REGRAS IMPORTANTES");
  const steps = Array.isArray(falAiConfig.prompt?.steps)
    ? falAiConfig.prompt.steps
    : [];
  const extraReferences = Array.isArray(falAiConfig.extraReferences)
    ? falAiConfig.extraReferences
    : [];
  const invalidTokens = Array.isArray(promptReferences?.invalidTokens)
    ? promptReferences.invalidTokens
    : [];
  const previewInvalidTokens = Array.from(
    new Set([
      ...invalidTokens,
      ...resolveFalAiPreviewPrompt(
        (falAiConfig.prompt?.base || "").trim() ||
          "Generate an image based on the reference",
        falAiConfig,
        previewSelections,
      ).invalidTokens,
    ]),
  );
  const detailSections = [];

  if (previewInvalidTokens.length > 0) {
    detailSections.push(
      `ATENÇÃO\n- Corrija os placeholders inválidos antes de salvar: ${previewInvalidTokens.join(", ")}.`,
    );
  } else if (promptReferences?.hasError) {
    detailSections.push(
      "ATENÇÃO\n- O Prompt ainda tem erro de validação. Corrija antes de salvar.",
    );
  }

  const operationalWarnings = buildFalAiOperationalWarnings(falAiConfig);

  if (operationalWarnings.length > 0) {
    detailSections.push(
      `ALERTAS\n${operationalWarnings.map((warning) => `- ${warning}`).join("\n")}`,
    );
  }

  const compactTokenLines = steps.map((step) => {
    if (!step?.id) {
      return "";
    }

    const token = createFalAiStepPlaceholderTemplate(step.id);
    const sourceLabel =
      step.type === "text"
        ? "texto digitado pelo participante"
        : step.type === "hidden"
          ? "opção sorteada automaticamente"
          : "opção escolhida pelo participante";
    const optionalText =
      step.required === false && step.type !== "hidden" ? "; pode ficar vazio" : "";
    const skipText = step?.skipIf?.stepId ? "; pode ser pulada por regra" : "";

    return `- ${token}: ${sourceLabel}${optionalText}${skipText}.`;
  }).filter(Boolean);

  detailSections.push(
    `VARIÁVEIS\n${compactTokenLines.length > 0
      ? compactTokenLines.join("\n")
      : "- Nenhuma variável configurada no Prompt."
    }`,
  );

  const previewStepStates = getFalAiPreviewStepStates(
    falAiConfig,
    previewSelections,
  );
  if (legacyRulesSection) {
    detailSections.push(`REGRAS IMPORTANTES\n${legacyRulesSection}`);
  }
  detailSections.push(
    "CENÁRIO DA PRÉVIA\n- As opções selecionadas acima resolvem os tokens como no upload.\n- Etapas ocultas, possibilidades adicionais com vários valores e textos livres ainda dependem do sorteio ou da resposta real do participante; a prévia usa valores determinísticos de exemplo.",
  );
  detailSections.push(
    [
      "CONFIGURAÇÃO DE GERAÇÃO",
      `- Modelo: ${falAiConfig.model || "fal-ai/nano-banana-pro/edit"}.`,
      `- Saída: ${falAiConfig.num_images || 1} imagem(ns), ${falAiConfig.aspect_ratio || "auto"}, ${falAiConfig.output_format || "png"}.`,
      `- Resolução: ${falAiConfig.model === "fal-ai/nano-banana/edit"
        ? "ignorada neste modelo"
        : falAiConfig.resolution || "1K"
      }.`,
      `- Limitar gerações: ${falAiConfig.limit_generations === true ? "sim" : "não"}; Busca na web: ${falAiConfig.model === "fal-ai/nano-banana-pro/edit" && falAiConfig.enable_web_search === true ? "sim" : "não"}.`,
    ].join("\n"),
  );

  return {
    configuredPrompt,
    previewOutput,
    optionControls: getFalAiPreviewOptionControls(
      falAiConfig,
      previewSelections,
    ),
    details: detailSections.join("\n\n"),
  };
};

const parseFalAiPromptReferences = (prompt = "") => {
  const steps = {};
  const stepOrder = [];
  const invalidTokens = [];

  const addInvalidToken = (token) => {
    if (token && !invalidTokens.includes(token)) {
      invalidTokens.push(token);
    }
  };

  const ensureStep = (id) => {
    if (!steps[id]) {
      steps[id] = {
        id,
        moreOptionOrder: [],
        moreOptionTokens: {},
      };
      stepOrder.push(id);
    }

    return steps[id];
  };

  const registerMoreOption = (stepId, label, rawToken) => {
    const step = ensureStep(stepId);

    if (!step.moreOptionTokens[label]) {
      step.moreOptionTokens[label] = [];
      step.moreOptionOrder.push(label);
    }

    if (!step.moreOptionTokens[label].includes(rawToken)) {
      step.moreOptionTokens[label].push(rawToken);
    }
  };

  prompt.replace(/\{([^}]+)\}/g, (match, rawContent) => {
    const token = rawContent.trim();

    if (!token) {
      addInvalidToken(match);
      return match;
    }

    if (token.includes("[") || token.includes("]")) {
      addInvalidToken(match);
      return match;
    }

    const nestedMatch = token.match(/^([^.{}\s]+)\.([^.{}\s]+)$/);

    if (nestedMatch) {
      const [, stepId, label] = nestedMatch;
      registerMoreOption(stepId, label, match);
      return match;
    }

    if (/^[^.{}\s]+$/.test(token)) {
      ensureStep(token);
      return match;
    }

    addInvalidToken(match);
    return match;
  });

  prompt.replace(/\[[^[\]]+\]/g, (match) => {
    addInvalidToken(match);
    return match;
  });

  return { stepOrder, steps, invalidTokens };
};

const syncFalAiStepMoreOptions = (
  stepId,
  promptLabels,
  currentMoreOptions = [],
  stepOptions = [],
  moreOptionCacheRef,
) => {
  if ((Array.isArray(stepOptions) ? stepOptions : []).length === 0) {
    return [];
  }

  const optionCache = moreOptionCacheRef.current;
  const currentOptionMap = {};
  const currentLabels = [];

  currentMoreOptions.forEach((option) => {
    const optionLabel = typeof option?.label === "string" ? option.label.trim() : "";

    if (!optionLabel || currentOptionMap[optionLabel]) {
      return;
    }

    currentOptionMap[optionLabel] = cloneFalAiValue(option);
    currentLabels.push(optionLabel);
  });

  const nextLabels = Array.from(new Set((promptLabels || []).filter(Boolean)));
  const addedLabels = nextLabels.filter((label) => !currentLabels.includes(label));
  const removedLabels = currentLabels.filter((label) => !nextLabels.includes(label));
  const workingOptionMap = { ...currentOptionMap };

  if (
    nextLabels.length === currentLabels.length &&
    addedLabels.length === 1 &&
    removedLabels.length === 1
  ) {
    const removedOption = workingOptionMap[removedLabels[0]];

    if (removedOption) {
      delete workingOptionMap[removedLabels[0]];
      workingOptionMap[addedLabels[0]] = {
        ...cloneFalAiValue(removedOption),
        label: addedLabels[0],
      };
    }
  }

  removedLabels.forEach((label) => {
    if (workingOptionMap[label]) {
      optionCache[getFalAiMoreOptionCacheKey(stepId, label)] = cloneFalAiValue(
        workingOptionMap[label],
      );
      delete workingOptionMap[label];
    }
  });

  return nextLabels.map((label) => {
    if (workingOptionMap[label]) {
      return normalizeFalAiStepMoreOptionsConfig(
        [{ ...cloneFalAiValue(workingOptionMap[label]), label }],
        stepOptions,
      )[0];
    }

    if (optionCache[getFalAiMoreOptionCacheKey(stepId, label)]) {
      return normalizeFalAiStepMoreOptionsConfig(
        [
          {
            ...cloneFalAiValue(
              optionCache[getFalAiMoreOptionCacheKey(stepId, label)],
            ),
            label,
          },
        ],
        stepOptions,
      )[0];
    }

    return createFalAiMoreOptionTemplate(label, stepOptions);
  });
};

const syncFalAiStepsFromPrompt = (
  parsedPrompt,
  currentSteps = [],
  stepCacheRef,
  moreOptionCacheRef,
) => {
  const stepCache = stepCacheRef.current;
  const currentStepMap = {};
  const currentStepIds = [];

  currentSteps.forEach((step) => {
    if (!step?.id) {
      return;
    }

    currentStepMap[step.id] = cloneFalAiValue(step);
    currentStepIds.push(step.id);
  });

  const nextStepIds = [...parsedPrompt.stepOrder];
  const addedStepIds = nextStepIds.filter((id) => !currentStepIds.includes(id));
  const removedStepIds = currentStepIds.filter((id) => !nextStepIds.includes(id));
  const workingStepMap = { ...currentStepMap };

  if (
    nextStepIds.length === currentStepIds.length &&
    addedStepIds.length === 1 &&
    removedStepIds.length === 1
  ) {
    const removedStep = workingStepMap[removedStepIds[0]] || stepCache[removedStepIds[0]];

    if (removedStep) {
      delete workingStepMap[removedStepIds[0]];
      workingStepMap[addedStepIds[0]] = {
        ...cloneFalAiValue(removedStep),
        id: addedStepIds[0],
      };
    }
  }

  removedStepIds.forEach((id) => {
    if (workingStepMap[id]) {
      stepCache[id] = cloneFalAiValue(workingStepMap[id]);
      delete workingStepMap[id];
    }
  });

  return nextStepIds.map((id) => {
    const parsedStep = parsedPrompt.steps[id] || { moreOptionOrder: [] };

    if (workingStepMap[id]) {
      const nextStep = normalizeFalAiStepConfig({
        ...cloneFalAiValue(workingStepMap[id]),
        id,
      });

      nextStep.moreOptions = syncFalAiStepMoreOptions(
        id,
        parsedStep.moreOptionOrder,
        nextStep.moreOptions || [],
        nextStep.options || [],
        moreOptionCacheRef,
      );

      return normalizeFalAiStepConfig(nextStep);
    }

    if (stepCache[id]) {
      const nextStep = normalizeFalAiStepConfig({
        ...cloneFalAiValue(stepCache[id]),
        id,
      });

      nextStep.moreOptions = syncFalAiStepMoreOptions(
        id,
        parsedStep.moreOptionOrder,
        nextStep.moreOptions || [],
        nextStep.options || [],
        moreOptionCacheRef,
      );

      return normalizeFalAiStepConfig(nextStep);
    }

    const nextStep = normalizeFalAiStepConfig(createFalAiStepTemplate(id));

    nextStep.moreOptions = syncFalAiStepMoreOptions(
      id,
      parsedStep.moreOptionOrder,
      nextStep.moreOptions || [],
      nextStep.options || [],
      moreOptionCacheRef,
    );

    return normalizeFalAiStepConfig(nextStep);
  });
};

const Photobooth = ({
  handleChange,
  values,
  setFieldValue,
  errors,
  touched,
  handlePreviewSuccess,
  handlePreviewError,
  dataTable,
  setDataAddForm,
  dataAddForm,
  fonts,
  setModal,
  applications,
  isSubmitting,
  handleCustomSubmit,
  setSubmitting,
  validateForm,
  setDataTableUpdate,
}) => {
  const notyf = useContext(NotyfContext);
  const [activeTab, setActiveTab] = useState("form");
  const [props, setProps] = useState({
    backgroundColor: values.json.apresentation?.textSide?.background.color,
    textColor: values.json.apresentation?.textSide?.textColor,
  });
  const [mediaConfigValidationRequest, setMediaConfigValidationRequest] =
    useState(null);

  useEffect(() => {
    const handleOpenMediaConfigModal = (event) => {
      const errorPaths = Array.isArray(event.detail?.errorPaths)
        ? event.detail.errorPaths
        : [];

      setActiveTab("form");
      setMediaConfigValidationRequest({
        id: Date.now(),
        errorPaths,
      });
    };

    document.addEventListener(
      "app10:open-media-config-modal",
      handleOpenMediaConfigModal,
    );

    return () => {
      document.removeEventListener(
        "app10:open-media-config-modal",
        handleOpenMediaConfigModal,
      );
    };
  }, []);

  useEffect(() => {
    notyf.open({
      type: "warning",
      message:
        "Esse aplicativo possui definições adicionais no modo apresentador",
      ripple: true,
      dismissible: true,
    });
  }, [notyf]);


  return (
    <>
      <Form.Group as={Col} className="textHeaderAplicativo mb-3">
        <h5 className="card-title mb-0">Definições</h5>
      </Form.Group>
      <Row className="mx-0 mb-3">
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Label>Título</Form.Label>
          <Form.Control
            name="titulo"
            value={values.titulo}
            onChange={handleChange}
            isInvalid={!!errors.titulo}
            isValid={touched.titulo && !errors.titulo}
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.titulo}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Form.Group as={Col} className="textHeaderAplicativo mb-3">
        <h5 className="card-title mb-0">Participante</h5>
      </Form.Group>
      <Row className="mx-0 mb-3">
        <p className="text-start fw-bold mb-2">Tela de resultado</p>
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Label>Título</Form.Label>
          <Form.Control
            as={"textarea"}
            name="json.result.title"
            value={values.json.result.title}
            onChange={handleChange}
            isInvalid={!!errors?.json?.result?.title}
            isValid={
              touched?.json?.result?.title && !errors.json?.result?.title
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors?.json?.result?.title}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Label>Descrição</Form.Label>
          <Form.Control
            as={"textarea"}
            name="json.result.description"
            value={values.json.result.description}
            onChange={handleChange}
            isInvalid={!!errors?.json?.result?.description}
            isValid={
              touched?.json?.result?.description &&
              !errors.json?.result?.description
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors?.json?.result?.description}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Form.Group as={Col} className="textHeaderAplicativo mb-3">
        <h5 className="card-title mb-0">Modo de apresentação</h5>
      </Form.Group>
      <Row className="mx-0">
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Tamanho fonte (M) (rem)</Form.Label>
          <Form.Control
            type="number"
            name="json.apresentation.textSide.h5"
            step={"0.01"}
            value={values.json.apresentation?.textSide?.h5}
            onChange={handleChange}
            isInvalid={!!errors.json?.apresentation?.textSide?.h5}
            isValid={
              touched.json?.apresentation?.textSide?.h5 &&
              !errors.json?.apresentation?.textSide?.h5
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.apresentation?.textSide?.h5}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Tamanho fonte (P) (rem)</Form.Label>
          <Form.Control
            type="number"
            name="json.apresentation.textSide.p"
            step={"0.01"}
            value={values.json.apresentation.textSide.p}
            onChange={handleChange}
            isInvalid={!!errors.json?.apresentation?.textSide?.p}
            isValid={
              touched.json?.apresentation?.textSide?.p &&
              !errors.json?.apresentation?.textSide?.p
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.apresentation?.textSide?.p}
          </Form.Control.Feedback>
        </Form.Group>
        <div className="col-md-4 mb-3">
          <Form.Label>Cor do texto</Form.Label>
          <div
            className={`d-flex justify-content-start align-items-center ${errors?.json?.apresentation?.textSide?.textColor ? "is-invalid" : ""}`}
          >
            <Form.Control
              type="hidden"
              name="json.apresentation.textSide.textColor"
              value={values.json?.apresentation?.textSide?.textColor}
              isInvalid={!!errors?.json?.apresentation?.textSide?.textColor}
              isValid={
                touched?.json?.apresentation?.textSide?.textColor &&
                !errors?.json?.apresentation?.textSide?.textColor
              }
            />
            <PopoverPicker
              color={props.textColor}
              onChangeX={(e) => {
                setFieldValue("json.apresentation.textSide.textColor", e);
                setProps((prevState) => ({
                  ...prevState,
                  textColor: e,
                }));
              }}
              classes={`me-1 picker ${errors?.json?.apresentation?.textSide?.textColor ? "is-invalid" : ""}`}
            />
          </div>
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors?.json?.apresentation?.textSide?.textColor
              ? errors?.json?.apresentation?.textSide?.textColor
              : ""}
          </Form.Control.Feedback>
        </div>
      </Row>

      <Row className="mx-0 mb-3">
        <Form.Group as={Col} md="6">
          <Form.Label>
            Tipo de plano de fundo{" "}
            {values.json.apresentation.textSide.background.type === "color" && (
              <OverlayTrigger
                overlay={
                  <Tooltip>
                    <b>Exemplo: </b>linear-gradient(90deg,rgba(42, 123, 155, 1)
                    0%, rgba(87, 199, 133, 1) 50%, rgba(237, 221, 83, 1) 100%)
                  </Tooltip>
                }
              >
                <span
                  className="badge text-dark bg-light cursor-pointer ms-1"
                  onClick={() => redirectURL(true, "https://cssgradient.io/")}
                >
                  CSS Gradient
                </span>
              </OverlayTrigger>
            )}
          </Form.Label>
          <Form.Select
            name="json.apresentation.textSide.background.type"
            onChange={handleChange}
            value={values.json.apresentation.textSide.background.type}
            isInvalid={!!errors.json?.apresentation?.textSide?.background?.type}
            isValid={
              touched.json?.apresentation?.textSide?.background?.type &&
              !errors.json?.apresentation?.textSide?.background?.type
            }
          >
            <option value={"color"}>Cor</option>
            <option value={"image"}>Imagem</option>
          </Form.Select>
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.apresentation?.textSide?.background?.type}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>

      {values.json?.apresentation?.textSide?.background?.type !== "color" ? (
        <Row className="mb-3 mx-0">
          <BackgroundFormComponent
            touched={touched}
            errors={errors}
            values={values}
            setFieldValue={setFieldValue}
            handlePreviewSuccess={handlePreviewSuccess}
            handlePreviewError={handlePreviewError}
          ></BackgroundFormComponent>
        </Row>
      ) : (
        <Row className="mx-0 mb-3">
          <div className="col-md-3 mb-3">
            <div
              className={`d-flex justify-content-start align-items-center ${errors?.json?.apresentation?.textSide?.background?.color ? "is-invalid" : ""}`}
            >
              <Form.Control
                type="hidden"
                name="json.apresentation.textSide.background.color"
                value={values.json.apresentation.textSide.background.color}
                isInvalid={
                  !!errors?.json?.apresentation?.textSide?.background?.color
                }
                isValid={
                  touched?.json?.apresentation?.textSide?.background?.color &&
                  !errors?.json?.apresentation?.textSide?.background?.color
                }
              />
              <PopoverPicker
                color={props.backgroundColor}
                onChangeX={(e) => {
                  setFieldValue(
                    "json.apresentation.textSide.background.color",
                    e,
                  );
                  setProps((prevState) => ({
                    ...prevState,
                    backgroundColor: e,
                  }));
                }}
                classes={`me-1 picker ${errors?.json?.apresentation?.textSide?.background?.color ? "is-invalid" : ""}`}
              />
            </div>
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors?.json?.apresentation?.textSide?.background?.color
                ? errors?.json?.apresentation?.textSide?.background?.color
                : ""}
            </Form.Control.Feedback>
          </div>
        </Row>
      )}

      <Row className="mx-0 mb-3">
        <p className="text-start fw-bold mb-2">Logomarca</p>
        <Form.Group as={Col} sm={6} className="mb-3">
          <Form.Label>Largura da logomarca (px)</Form.Label>
          <Form.Control
            type="number"
            name="json.apresentation.configs.logoSize"
            value={values.json.apresentation.configs.logoSize}
            onChange={(e) => {
              handleChange(e);
            }}
            isInvalid={!!errors.json?.apresentation?.configs?.logoSize}
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.apresentation?.configs?.logoSize}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mx-0">
        <div className="tab">
          <Tab.Container
            id="left-tabs-example"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
          >
            <Nav variant="tabs" className="">
              <Nav.Item>
                <Nav.Link eventKey={"form"}>Captura de mídia</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={"default"}>Galeria</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={"applications"}>
                  Biblioteca de imagens
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey={"form"} className="mt-3">
                <PhotoboothPresentation
                  dataTable={dataTable}
                  values={values}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setDataAddForm={setDataAddForm}
                  dataAddForm={dataAddForm}
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  fonts={fonts}
                  setModal={setModal}
                  applications={applications}
                  mediaConfigValidationRequest={mediaConfigValidationRequest}
                ></PhotoboothPresentation>
              </Tab.Pane>
              <Tab.Pane eventKey={"default"} className="mt-3">
                <GalleryPresentation
                  dataTable={dataTable}
                  values={values}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setDataAddForm={setDataAddForm}
                  dataAddForm={dataAddForm}
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  fonts={fonts}
                  setModal={setModal}
                  applications={applications}
                ></GalleryPresentation>
              </Tab.Pane>
              <Tab.Pane eventKey={"applications"} className="mt-3">
                <ImageLibrary
                  dataTable={dataTable}
                  values={values}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setDataAddForm={setDataAddForm}
                  dataAddForm={dataAddForm}
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  fonts={fonts}
                  setModal={setModal}
                  applications={applications}
                ></ImageLibrary>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </Row>
    </>
  );
};

const FAL_AI_PROMPT_GUIDE_BASE_PROMPT =
  "Substitua a pessoa da imagem de referência pela pessoa da foto do participante, preservando a identidade, o rosto e a pose do participante. Crie uma fotografia editorial usando {roupa} em um cenário {cenário}, com iluminação coerente com o ambiente e acabamento de capa de revista.";
const FAL_AI_PROMPT_GUIDE_EXTENDED_PROMPT =
  "Substitua a pessoa da imagem de referência pela pessoa da foto do participante, preservando a identidade, o rosto e a pose do participante. Crie uma fotografia editorial usando {roupa} em um cenário {ambiente}, com iluminação coerente com o ambiente e acabamento de capa de revista. Na roupa, defina a cor {roupa.cor} e a textura {roupa.textura}, em um ambiente {ambiente}, durante {ambiente.horario}, com iluminação {ambiente.luz}.";

const FAL_AI_PROMPT_GUIDE_EXAMPLES = [
  {
    title: "1. Prompt-base",
    description:
      "A ideia visual de troca de pessoa fica descrita no próprio Prompt.",
    prompt: FAL_AI_PROMPT_GUIDE_BASE_PROMPT,
  },
  {
    title: "2. O mesmo Prompt com duas etapas",
    description:
      "As variáveis roupa e cenário podem ser configuradas como duas etapas, com duas opções em cada uma.",
    prompt: FAL_AI_PROMPT_GUIDE_BASE_PROMPT,
  },
  {
    title: "3. O mesmo Prompt com duas opções",
    description:
      "Cada variável pode receber duas opções e substituir apenas o trecho correspondente do Prompt.",
    prompt: FAL_AI_PROMPT_GUIDE_BASE_PROMPT,
  },
  {
    title: "4. O mesmo Prompt com elementos adicionais",
    description:
      "A frase-base recebe informações complementares associadas às opções escolhidas.",
    prompt: FAL_AI_PROMPT_GUIDE_EXTENDED_PROMPT,
  },
  {
    title: "5. O mesmo Prompt com mais elementos adicionais",
    description:
      "O mesmo padrão pode ser usado para preencher vários tokens compostos em duas etapas.",
    prompt: FAL_AI_PROMPT_GUIDE_EXTENDED_PROMPT,
  },
  {
    title: "6. O mesmo Prompt com elementos adicionais e referências",
    description:
      "O próprio Prompt define o papel de cada referência configurada, sem adicionar um mapa automático de imagens.",
    prompt: `${FAL_AI_PROMPT_GUIDE_EXTENDED_PROMPT} Use a primeira referência configurada com imagem para orientar o cenário e a segunda referência configurada com imagem para orientar o figurino. Preserve a identidade e as características faciais da pessoa da foto do participante e integre cenário, pessoa e figurino de forma realista.`,
  },
];

const FalAiPromptGuideModal = ({ show, onHide }) => (
  <Modal
    show={show}
    onHide={onHide}
    size="lg"
    fullscreen="sm-down"
    centered
    scrollable
    backdrop="static"
    className="nested-modal"
    style={{ zIndex: 1080 }}
    backdropClassName="nested-modal-backdrop-lv3"
  >
    <Modal.Header closeButton>
      <Modal.Title>Guia de Prompts do Photobooth</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {FAL_AI_PROMPT_GUIDE_EXAMPLES.map((example) => (
        <Card className="border bg-light mb-3" key={example.title}>
          <Card.Body>
            <Card.Title as="h6">{example.title}</Card.Title>
            <Card.Text className="text-muted small mb-2">
              {example.description}
            </Card.Text>
            <div className="fw-semibold small mb-1">Prompt de exemplo</div>
            <pre
              className="bg-white border rounded p-2 small mb-2"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {example.prompt}
            </pre>
          </Card.Body>
        </Card>
      ))}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="primary" onClick={onHide}>
        Entendi
      </Button>
    </Modal.Footer>
  </Modal>
);

const FalAiPromptScoreBreakdown = ({ criteria = [] }) => {
  if (!Array.isArray(criteria) || criteria.length === 0) {
    return null;
  }

  return (
    <div className="mt-3">
      <p className="fw-semibold mb-2 small">Critérios da pontuação</p>
      <div className="small">
        {criteria.map((criterion) => {
          const score =
            criterion?.score === null || criterion?.score === undefined
              ? "—"
              : `${criterion.score}/${criterion.maxScore}`;

          return (
            <div className="mb-2" key={criterion?.key || criterion?.label}>
              <div className="d-flex justify-content-between gap-2">
                <span className="fw-semibold">
                  {criterion?.label || criterion?.key || "Critério"}
                </span>
                <span className="text-muted text-nowrap">{score}</span>
              </div>
              {criterion?.explanation && (
                <div className="text-muted">{criterion.explanation}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PhotoboothPresentation = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  handlePreviewSuccess,
  handlePreviewError,
  mediaConfigValidationRequest,
}) => {
  const notyf = useContext(NotyfContext);
  const [activeFalAiStepAccordion, setActiveFalAiStepAccordion] = useState("0");
  const [activeFalAiReferenceAccordion, setActiveFalAiReferenceAccordion] =
    useState("0");
  const [falAiPromptFeedback, setFalAiPromptFeedback] = useState(null);
  const [falAiPromptFeedbackError, setFalAiPromptFeedbackError] = useState("");
  const [
    falAiPromptFeedbackConfigSnapshot,
    setFalAiPromptFeedbackConfigSnapshot,
  ] = useState("");
  const [
    falAiPromptFeedbackSelectionSnapshot,
    setFalAiPromptFeedbackSelectionSnapshot,
  ] = useState("");
  const [hasAppliedFalAiPromptFeedback, setHasAppliedFalAiPromptFeedback] =
    useState(false);
  const [isFalAiPromptFeedbackLoading, setIsFalAiPromptFeedbackLoading] =
    useState(false);
  const [showFalAiPromptChatModal, setShowFalAiPromptChatModal] =
    useState(false);
  const [falAiPromptChatDraft, setFalAiPromptChatDraft] = useState("");
  const [falAiPromptChatSubmittedPrompt, setFalAiPromptChatSubmittedPrompt] =
    useState("");
  const [showFalAiPromptFullPrompt, setShowFalAiPromptFullPrompt] =
    useState(false);
  const [falAiPromptFullPrompt, setFalAiPromptFullPrompt] = useState("");
  const [falAiPromptFullPromptError, setFalAiPromptFullPromptError] =
    useState("");
  const [isFalAiPromptFullPromptLoading, setIsFalAiPromptFullPromptLoading] =
    useState(false);
  const [showMediaConfigModal, setShowMediaConfigModal] = useState(false);
  const [showFalAiPromptGuideModal, setShowFalAiPromptGuideModal] =
    useState(false);
  const [mediaConfigSnapshot, setMediaConfigSnapshot] = useState(null);
  const [falAiPreviewSelectionState, setFalAiPreviewSelectionState] =
    useState({});
  const falAiStepCacheRef = useRef({});
  const falAiMoreOptionCacheRef = useRef({});
  const falAiPromptBaseTextareaRef = useRef(null);
  const falAiPromptBaseSelectionRef = useRef({ start: 0, end: 0 });
  const handledMediaConfigValidationRequestRef = useRef(null);

  const currentFalAiConfig = useMemo(
    () => values.json.apresentation.configs.falai || {},
    [values.json.apresentation.configs.falai],
  );

  const syncFalAiPromptConfig = useCallback((promptValue) => {
    const falAiConfig = values.json.apresentation.configs.falai || {};
    const parsedPrompt = parseFalAiPromptReferences(promptValue);
    const nextSteps = syncFalAiStepsFromPrompt(
      parsedPrompt,
      falAiConfig.prompt?.steps || [],
      falAiStepCacheRef,
      falAiMoreOptionCacheRef,
    );

    if (
      JSON.stringify(nextSteps) !==
      JSON.stringify(falAiConfig.prompt?.steps || [])
    ) {
      setFieldValue("json.apresentation.configs.falai.prompt.steps", nextSteps);
    }

    if (Array.isArray(falAiConfig.groupedOptions) && falAiConfig.groupedOptions.length > 0) {
      setFieldValue("json.apresentation.configs.falai.groupedOptions", []);
    }
  }, [setFieldValue, values.json.apresentation.configs.falai]);

  const falAiPromptBase =
    values.json.apresentation.configs.falai?.prompt?.base || "";
  const falAiPromptSteps = useMemo(
    () => values.json.apresentation.configs.falai?.prompt?.steps || [],
    [values.json.apresentation.configs.falai?.prompt?.steps],
  );
  const falAiPromptReferences = parseFalAiPromptReferences(falAiPromptBase);
  const falAiPromptChatReferences = parseFalAiPromptReferences(
    falAiPromptChatDraft,
  );
  const falAiPromptChatError = getFalAiPromptBaseFeedbackMessage({
    invalidTokens: falAiPromptChatReferences.invalidTokens,
  });
  const falAiPromptBaseError = getFalAiPromptBaseFeedbackMessage({
    invalidTokens: falAiPromptReferences.invalidTokens,
    schemaError: errors?.json?.apresentation?.configs?.falai?.prompt?.base || "",
  });
  const falAiPreviewSelections = useMemo(
    () =>
      normalizeFalAiPreviewSelections(
        currentFalAiConfig,
        falAiPreviewSelectionState,
      ),
    [currentFalAiConfig, falAiPreviewSelectionState],
  );
  const falAiPromptPreview = buildFalAiPromptPreviewCards(
    currentFalAiConfig,
    {
      ...falAiPromptReferences,
      hasError: Boolean(falAiPromptBaseError),
    },
    falAiPreviewSelections,
  );
  const falAiPromptPreviewOutput =
    falAiPromptPreview.previewOutput || falAiPromptBase;
  const currentFalAiPreviewSelectionSnapshot = JSON.stringify(
    falAiPreviewSelections,
  );
  const falAiPromptFeedbackWarnings = Array.isArray(falAiPromptFeedback?.warnings)
    ? falAiPromptFeedback.warnings
    : [];
  const falAiPromptFeedbackSuggestions = Array.isArray(
    falAiPromptFeedback?.suggestions,
  )
    ? falAiPromptFeedback.suggestions
    : [];
  const currentFalAiConfigSnapshot = JSON.stringify(currentFalAiConfig);
  const falAiOptimizedConfig =
    falAiPromptFeedback?.optimizedConfig &&
      typeof falAiPromptFeedback.optimizedConfig === "object"
      ? falAiPromptFeedback.optimizedConfig
      : null;
  const falAiOptimizedConfigError = falAiOptimizedConfig
    ? getFalAiOptimizedConfigError(
      currentFalAiConfig,
      falAiOptimizedConfig,
    )
    : "";
  const isFalAiPromptFeedbackStale = Boolean(
    falAiPromptFeedback &&
    ((falAiPromptFeedbackConfigSnapshot &&
      falAiPromptFeedbackConfigSnapshot !== currentFalAiConfigSnapshot) ||
      (falAiPromptFeedbackSelectionSnapshot &&
        falAiPromptFeedbackSelectionSnapshot !==
          currentFalAiPreviewSelectionSnapshot)),
  );
  const isFalAiPromptFeedbackStructurallyApplicable = Boolean(
    !isFalAiPromptFeedbackLoading &&
    falAiPromptFeedback?.scoreComplete !== false &&
    falAiPromptFeedback?.applicable !== false &&
    falAiOptimizedConfig &&
    !falAiOptimizedConfigError &&
    !isFalAiPromptFeedbackStale,
  );
  const falAiProposedConfig = useMemo(
    () =>
      isFalAiPromptFeedbackStructurallyApplicable
        ? buildAppliedFalAiConfig(
          currentFalAiConfig,
          falAiOptimizedConfig,
          falAiPromptFeedback.rewrittenPrompt,
        )
        : null,
    [
      currentFalAiConfig,
      falAiOptimizedConfig,
      falAiPromptFeedback?.rewrittenPrompt,
      isFalAiPromptFeedbackStructurallyApplicable,
    ],
  );
  const falAiProposedChanges = useMemo(
    () =>
      falAiProposedConfig
        ? buildFalAiConfigChangeSummary(
          currentFalAiConfig,
          falAiProposedConfig,
        )
        : [],
    [currentFalAiConfig, falAiProposedConfig],
  );
  const falAiPromptApplicationMessage = isFalAiPromptFeedbackLoading
    ? "A análise ainda está em andamento."
    : !falAiPromptFeedback
      ? "Analise o prompt antes de aplicar sugestões."
      : isFalAiPromptFeedbackStale
        ? "A configuração ou a seleção da prévia foi alterada depois da análise. Analise novamente antes de aplicar."
        : falAiPromptFeedback?.scoreComplete === false
          ? "A IA retornou uma pontuação incompleta. Analise novamente antes de aplicar."
          : falAiPromptFeedback?.applicable === false
            ? falAiPromptFeedback.applicationError ||
            "A IA não retornou sugestões que possam ser aplicadas com segurança."
            : !falAiOptimizedConfig
              ? "A IA não retornou uma configuração otimizada completa."
              : falAiOptimizedConfigError
                ? falAiOptimizedConfigError
                : !falAiProposedConfig
                  ? "Não foi possível preparar as sugestões para aplicação."
                  : falAiProposedChanges.length === 0
                    ? "A análise não sugeriu alterações aplicáveis."
                    : "";
  const canAnalyzeFalAiPrompt =
    !isFalAiPromptFeedbackLoading &&
    !falAiPromptBaseError &&
    falAiPromptBase.trim() !== "";
  const canSubmitFalAiPromptChat =
    !isFalAiPromptFeedbackLoading &&
    !falAiPromptChatError &&
    falAiPromptChatDraft.trim() !== "";
  const canApplyFalAiPromptFeedback =
    isFalAiPromptFeedbackStructurallyApplicable &&
    falAiProposedChanges.length > 0;

  const openMediaConfigModal = useCallback(() => {
    setMediaConfigSnapshot(
      cloneFalAiValue(values.json.apresentation.configs || {}),
    );
    setShowMediaConfigModal(true);
    setShowFalAiPromptGuideModal(
      values.json.apresentation.configs.media === "image-falai",
    );
  }, [values.json.apresentation.configs]);

  const closeMediaConfigModal = useCallback(() => {
    if (mediaConfigSnapshot) {
      setFieldValue(
        "json.apresentation.configs",
        cloneFalAiValue(mediaConfigSnapshot),
      );
    }

    setShowMediaConfigModal(false);
    setShowFalAiPromptGuideModal(false);
    setShowFalAiPromptChatModal(false);
    setShowFalAiPromptFullPrompt(false);
    setMediaConfigSnapshot(null);
    setFalAiPromptFeedback(null);
    setFalAiPromptFeedbackError("");
    setFalAiPromptFeedbackConfigSnapshot("");
    setHasAppliedFalAiPromptFeedback(false);
  }, [mediaConfigSnapshot, setFieldValue]);

  const commitMediaConfigModal = useCallback(() => {
    setShowMediaConfigModal(false);
    setShowFalAiPromptGuideModal(false);
    setShowFalAiPromptChatModal(false);
    setShowFalAiPromptFullPrompt(false);
    setMediaConfigSnapshot(null);
  }, []);

  const saveMediaConfigModal = useCallback(() => {
    const currentConfigs = values.json.apresentation.configs || {};
    const falAiConfig = currentConfigs.falai || {};
    const operationalWarnings =
      currentConfigs.media === "image-falai"
        ? buildFalAiOperationalWarnings(falAiConfig)
        : [];

    if (operationalWarnings.length === 0) {
      commitMediaConfigModal();
      return;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar custo operacional"
          description={
            <span style={{ whiteSpace: "pre-line" }}>
              {[...operationalWarnings, "", "Deseja continuar?"].join("\n")}
            </span>
          }
          textConfirm="Confirmar"
          textCancel="Voltar"
          action={commitMediaConfigModal}
          onClose={onClose}
        />
      ),
    });
  }, [commitMediaConfigModal, values.json.apresentation.configs]);

  useEffect(() => {
    if (!mediaConfigValidationRequest) {
      return;
    }

    const validationRequestKey =
      mediaConfigValidationRequest.id || mediaConfigValidationRequest;

    if (
      handledMediaConfigValidationRequestRef.current === validationRequestKey
    ) {
      return;
    }

    handledMediaConfigValidationRequestRef.current = validationRequestKey;

    const errorPaths = Array.isArray(mediaConfigValidationRequest.errorPaths)
      ? mediaConfigValidationRequest.errorPaths
      : [];
    const falAiStepErrorPath = errorPaths.find((errorPath) =>
      String(errorPath || "").includes(
        "json.apresentation.configs.falai.prompt.steps[",
      ),
    );
    const falAiStepMatch = falAiStepErrorPath?.match(
      /falai\.prompt\.steps\[(\d+)\]/,
    );
    const falAiReferenceErrorPath = errorPaths.find((errorPath) =>
      String(errorPath || "").includes(
        "json.apresentation.configs.falai.extraReferences[",
      ),
    );
    const falAiReferenceMatch = falAiReferenceErrorPath?.match(
      /falai\.extraReferences\[(\d+)\]/,
    );

    if (falAiStepMatch?.[1]) {
      setActiveFalAiStepAccordion(falAiStepMatch[1]);
    }

    if (falAiReferenceMatch?.[1]) {
      setActiveFalAiReferenceAccordion(falAiReferenceMatch[1]);
    }

    if (!showMediaConfigModal) {
      openMediaConfigModal();
    }
  }, [
    mediaConfigValidationRequest,
    openMediaConfigModal,
    showMediaConfigModal,
  ]);

  const updateFalAiStepOptions = useCallback((stepIdx, updater) => {
    const nextSteps = cloneFalAiValue(
      values.json.apresentation.configs.falai?.prompt?.steps || [],
    );
    const currentStep = normalizeFalAiStepConfig(nextSteps?.[stepIdx] || {});
    const nextOptions = updater(currentStep.options || []);

    nextSteps[stepIdx] = normalizeFalAiStepConfig({
      ...currentStep,
      options: nextOptions,
    });

    setFieldValue("json.apresentation.configs.falai.prompt.steps", nextSteps);
  }, [setFieldValue, values.json.apresentation.configs.falai?.prompt?.steps]);

  const updateFalAiMoreOptionPossibilityValues = useCallback((
    stepIdx,
    moreOptionIdx,
    stepOptions,
    optionId,
    updater,
  ) => {
    const currentMoreOption =
      values.json.apresentation.configs.falai?.prompt?.steps?.[stepIdx]
        ?.moreOptions?.[moreOptionIdx];
    const normalizedMoreOption =
      normalizeFalAiStepMoreOptionsConfig(
        [
          currentMoreOption ||
          createFalAiMoreOptionTemplate("campo1", stepOptions),
        ],
        stepOptions,
      )[0] || createFalAiMoreOptionTemplate("campo1", stepOptions);
    const possibilityIdx = normalizedMoreOption.possibilities.findIndex(
      (possibility) =>
        getFalAiMoreOptionPossibilityId(possibility) === optionId,
    );

    if (possibilityIdx === -1) {
      return;
    }

    const nextPossibilities = [...normalizedMoreOption.possibilities];
    const currentValues = getFalAiMoreOptionPossibilityValues(
      nextPossibilities[possibilityIdx],
    );

    nextPossibilities[possibilityIdx] = {
      ...nextPossibilities[possibilityIdx],
      values: updater([...currentValues]),
    };

    setFieldValue(
      `json.apresentation.configs.falai.prompt.steps[${stepIdx}].moreOptions[${moreOptionIdx}].possibilities`,
      nextPossibilities,
    );
  }, [setFieldValue, values.json.apresentation.configs.falai?.prompt?.steps]);

  const updateFalAiPromptBase = useCallback((nextPromptValue) => {
    setFieldValue("json.apresentation.configs.falai.prompt.base", nextPromptValue);
    syncFalAiPromptConfig(nextPromptValue);
  }, [setFieldValue, syncFalAiPromptConfig]);

  const updateFalAiPreviewSelection = useCallback((stepId, value) => {
    setFalAiPreviewSelectionState((currentSelections) => ({
      ...currentSelections,
      [stepId]: value,
    }));
  }, []);

  const openFalAiPromptChatModal = useCallback(() => {
    if (!canAnalyzeFalAiPrompt) {
      return;
    }

    setFalAiPromptChatDraft(falAiPromptPreviewOutput);
    setFalAiPromptChatSubmittedPrompt("");
    setShowFalAiPromptFullPrompt(false);
    setFalAiPromptFullPrompt("");
    setFalAiPromptFullPromptError("");
    setFalAiPromptFeedback(null);
    setFalAiPromptFeedbackError("");
    setFalAiPromptFeedbackConfigSnapshot("");
    setFalAiPromptFeedbackSelectionSnapshot("");
    setHasAppliedFalAiPromptFeedback(false);
    setShowFalAiPromptChatModal(true);
  }, [canAnalyzeFalAiPrompt, falAiPromptPreviewOutput]);

  const resetFalAiPromptChat = useCallback(() => {
    setFalAiPromptChatDraft(falAiPromptPreviewOutput);
    setFalAiPromptChatSubmittedPrompt("");
    setShowFalAiPromptFullPrompt(false);
    setFalAiPromptFullPrompt("");
    setFalAiPromptFullPromptError("");
    setFalAiPromptFeedback(null);
    setFalAiPromptFeedbackError("");
    setFalAiPromptFeedbackConfigSnapshot("");
    setFalAiPromptFeedbackSelectionSnapshot("");
    setHasAppliedFalAiPromptFeedback(false);
  }, [falAiPromptPreviewOutput]);

  const updateFalAiPromptChatDraft = useCallback((value) => {
    setFalAiPromptChatDraft(value);
    setShowFalAiPromptFullPrompt(false);
    setFalAiPromptFullPrompt("");
    setFalAiPromptFullPromptError("");
  }, []);

  const toggleFalAiPromptFullPrompt = useCallback(async () => {
    if (showFalAiPromptFullPrompt) {
      setShowFalAiPromptFullPrompt(false);
      return;
    }

    if (!canSubmitFalAiPromptChat) {
      return;
    }

    setIsFalAiPromptFullPromptLoading(true);
    setFalAiPromptFullPromptError("");

    const requestedFalAiConfig = cloneFalAiValue(
      values.json.apresentation.configs.falai || {},
    );
    requestedFalAiConfig.prompt = {
      ...(requestedFalAiConfig.prompt || {}),
      base: falAiPromptChatDraft.trim(),
    };

    try {
      const response = await axios.post("/r1/fal-ai/prompt-feedback/preview", {
        customizacao_id: values.customizacao_id,
        falaiConfig: requestedFalAiConfig,
        promptPreview: falAiPromptChatDraft.trim(),
        previewSelections: falAiPreviewSelections,
      });
      const fullPrompt = response.data?.fullPrompt || "";

      if (!fullPrompt) {
        throw new Error("O servidor não retornou o prompt completo.");
      }

      setFalAiPromptFullPrompt(fullPrompt);
      setShowFalAiPromptFullPrompt(true);
    } catch (error) {
      setFalAiPromptFullPromptError(
        error?.response?.data?.message ||
          error?.message ||
          "Não foi possível carregar o prompt completo.",
      );
    } finally {
      setIsFalAiPromptFullPromptLoading(false);
    }
  }, [
    canSubmitFalAiPromptChat,
    falAiPromptChatDraft,
    falAiPreviewSelections,
    showFalAiPromptFullPrompt,
    values.customizacao_id,
    values.json.apresentation.configs.falai,
  ]);

  const rememberFalAiPromptBaseSelection = useCallback((event) => {
    if (
      event?.target &&
      typeof event.target.selectionStart === "number" &&
      typeof event.target.selectionEnd === "number"
    ) {
      falAiPromptBaseSelectionRef.current = {
        start: event.target.selectionStart,
        end: event.target.selectionEnd,
      };
    }
  }, []);

  const insertFalAiPromptVariable = useCallback(() => {
    const currentPrompt = falAiPromptBase || "";
    const existingIds = new Set(
      parseFalAiPromptReferences(currentPrompt).stepOrder,
    );
    let nextVariableId = "variavel";
    let variableIndex = 2;

    while (existingIds.has(nextVariableId)) {
      nextVariableId = `variavel${variableIndex}`;
      variableIndex += 1;
    }

    const token = createFalAiStepPlaceholderTemplate(nextVariableId);
    const selection = falAiPromptBaseSelectionRef.current || {};
    const start = Math.max(
      0,
      Math.min(
        Number.isInteger(selection.start) ? selection.start : currentPrompt.length,
        currentPrompt.length,
      ),
    );
    const end = Math.max(
      start,
      Math.min(
        Number.isInteger(selection.end) ? selection.end : start,
        currentPrompt.length,
      ),
    );
    const nextPromptValue =
      currentPrompt.slice(0, start) + token + currentPrompt.slice(end);
    const nextCursorPosition = start + token.length;

    updateFalAiPromptBase(nextPromptValue);
    falAiPromptBaseSelectionRef.current = {
      start: nextCursorPosition,
      end: nextCursorPosition,
    };

    window.requestAnimationFrame(() => {
      const promptTextarea = falAiPromptBaseTextareaRef.current;

      if (!promptTextarea) {
        return;
      }

      promptTextarea.focus();
      promptTextarea.setSelectionRange(nextCursorPosition, nextCursorPosition);
    });
  }, [falAiPromptBase, updateFalAiPromptBase]);

  const handleFalAiPromptFeedback = useCallback(async () => {
    if (!canSubmitFalAiPromptChat) {
      return;
    }

    const requestedPrompt = falAiPromptChatDraft.trim();
    setIsFalAiPromptFeedbackLoading(true);
    setFalAiPromptFeedbackError("");
    setFalAiPromptFeedback(null);
    setFalAiPromptFeedbackConfigSnapshot("");
    setFalAiPromptFeedbackSelectionSnapshot("");
    setHasAppliedFalAiPromptFeedback(false);

    const requestedFalAiConfig = cloneFalAiValue(
      values.json.apresentation.configs.falai || {},
    );
    const requestedFalAiConfigSnapshot = JSON.stringify(
      requestedFalAiConfig,
    );
    const requestedFalAiSelectionSnapshot = JSON.stringify(
      falAiPreviewSelections,
    );
    setFalAiPromptChatSubmittedPrompt(requestedPrompt);

    try {
      const response = await axios.post("/r1/fal-ai/prompt-feedback", {
        customizacao_id: values.customizacao_id,
        falaiConfig: requestedFalAiConfig,
        promptPreview: requestedPrompt,
        previewSelections: falAiPreviewSelections,
      });

      const feedback = response.data?.feedback || null;
      if (response.data?.fullPrompt) {
        setFalAiPromptFullPrompt(response.data.fullPrompt);
      }

      if (!feedback) {
        setFalAiPromptChatSubmittedPrompt("");
        setFalAiPromptFeedbackError(
          "O modelo não retornou uma resposta para este prompt.",
        );
      }

      setFalAiPromptFeedback(feedback);
      setFalAiPromptFeedbackConfigSnapshot(
        feedback ? requestedFalAiConfigSnapshot : "",
      );
      setFalAiPromptFeedbackSelectionSnapshot(
        feedback ? requestedFalAiSelectionSnapshot : "",
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Não foi possível analisar o prompt.";

      setFalAiPromptFeedback(null);
      setFalAiPromptChatSubmittedPrompt("");
      setFalAiPromptFeedbackConfigSnapshot("");
      setFalAiPromptFeedbackSelectionSnapshot("");
      setHasAppliedFalAiPromptFeedback(false);
      setFalAiPromptFeedbackError(message);
      notyf.open({
        type: "danger",
        message,
        ripple: true,
        dismissible: true,
      });
    } finally {
      setIsFalAiPromptFeedbackLoading(false);
    }
  }, [
    canSubmitFalAiPromptChat,
    falAiPromptChatDraft,
    falAiPreviewSelections,
    notyf,
    values.customizacao_id,
    values.json.apresentation.configs.falai,
  ]);

  const handleApplyFalAiPromptFeedback = useCallback(() => {
    if (
      !canApplyFalAiPromptFeedback ||
      !falAiProposedConfig ||
      falAiProposedChanges.length === 0
    ) {
      notyf.open({
        type: "warning",
        message: `Não é possível aplicar as sugestões: ${falAiPromptApplicationMessage ||
          "nenhuma sugestão aplicável está disponível."
          }`,
        ripple: true,
        dismissible: true,
      });
      return;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Aplicar sugestões"
          description={
            <span style={{ whiteSpace: "pre-line" }}>
              {[
                "Campos que serão alterados:",
                ...falAiProposedChanges.map((item) => `- ${item}`),
                "",
                "As alterações ainda precisarão ser salvas no formulário.",
              ].join("\n")}
            </span>
          }
          textConfirm="Aplicar sugestões"
          action={() => {
            setFieldValue(
              "json.apresentation.configs.falai",
              falAiProposedConfig,
            );
            setFalAiPromptFeedbackConfigSnapshot(
              JSON.stringify(falAiProposedConfig),
            );
            setHasAppliedFalAiPromptFeedback(true);
            setFalAiPromptFeedbackError("");
            notyf.open({
              type: "success",
              message:
                "Configuração fal.ai aplicada. Revise as alterações antes de salvar.",
              ripple: true,
              dismissible: true,
            });
          }}
          onClose={onClose}
        />
      ),
    });
  }, [
    canApplyFalAiPromptFeedback,
    falAiPromptApplicationMessage,
    falAiProposedChanges,
    falAiProposedConfig,
    notyf,
    setFieldValue,
  ]);

  const handleAddFalAiMoreOption = useCallback((stepId, currentMoreOptions = []) => {
    const nextLabel = getNextFalAiMoreOptionLabel(currentMoreOptions);
    const nextToken = createFalAiNestedPlaceholderTemplate(stepId, nextLabel);
    const nextPromptValue = falAiPromptBase.includes(nextToken)
      ? falAiPromptBase
      : `${falAiPromptBase}${falAiPromptBase && !/\s$/.test(falAiPromptBase) ? " " : ""
        }${nextToken}`.trim();

    updateFalAiPromptBase(nextPromptValue);
  }, [falAiPromptBase, updateFalAiPromptBase]);

  const handleRemoveFalAiMoreOption = useCallback((stepId, label) => {
    const currentToken = createFalAiNestedPlaceholderTemplate(stepId, label);
    const nextPromptValue = falAiPromptBase
      .split(currentToken)
      .join("")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    updateFalAiPromptBase(nextPromptValue);
  }, [falAiPromptBase, updateFalAiPromptBase]);

  const handleFalAiMoreOptionLabelChange = useCallback((stepIdx, optionIdx, nextLabel) => {
    const currentStep = falAiPromptSteps?.[stepIdx];
    const currentLabel = currentStep?.moreOptions?.[optionIdx]?.label || "";

    setFieldValue(
      `json.apresentation.configs.falai.prompt.steps[${stepIdx}].moreOptions[${optionIdx}].label`,
      nextLabel,
    );

    if (!currentStep?.id || currentLabel === nextLabel) {
      return;
    }

    const nextPromptValue = replaceFalAiPromptToken(
      falAiPromptBase,
      createFalAiNestedPlaceholderTemplate(currentStep.id, currentLabel),
      createFalAiNestedPlaceholderTemplate(currentStep.id, nextLabel),
    );

    updateFalAiPromptBase(nextPromptValue);
  }, [falAiPromptBase, falAiPromptSteps, setFieldValue, updateFalAiPromptBase]);

  useEffect(() => {
    syncFalAiPromptConfig(falAiPromptBase);
  }, [falAiPromptBase, syncFalAiPromptConfig]);

  useEffect(() => {
    if (!Array.isArray(falAiPromptSteps) || falAiPromptSteps.length === 0) {
      return;
    }

    const normalizedSteps = falAiPromptSteps.map((step) =>
      normalizeFalAiStepConfig(step),
    );

    const nextSteps = normalizedSteps.map((step, stepIdx) => {
      const skipSourceSteps = normalizedSteps.filter(
        (candidate, candidateIdx) =>
          candidateIdx < stepIdx && candidate?.type !== "hidden",
      );
      const hasSkipIfConfig = Boolean(
        step?.skipIf &&
        (step.skipIf.stepId || step.skipIf.value || step.skipIf.stepId === ""),
      );

      if (!hasSkipIfConfig) {
        return step?.skipIf ? { ...cloneFalAiValue(step), skipIf: null } : step;
      }

      const canConfigureSkipIf =
        step?.required !== false && skipSourceSteps.length > 0;
      const hasValidSkipSource = skipSourceSteps.some(
        (candidate) => candidate.id === step?.skipIf?.stepId,
      );

      if (!canConfigureSkipIf || !hasValidSkipSource) {
        return { ...cloneFalAiValue(step), skipIf: null };
      }

      const skipSourceStep = skipSourceSteps.find(
        (candidate) => candidate.id === step?.skipIf?.stepId,
      );

      if (skipSourceStep?.type !== "text") {
        const availableOptionIds = new Set(
          (skipSourceStep?.options || [])
            .map((option) => getFalAiOptionId(option))
            .filter(Boolean),
        );

        if (!availableOptionIds.has(step?.skipIf?.value)) {
          return {
            ...cloneFalAiValue(step),
            skipIf: {
              stepId: step.skipIf.stepId,
              value: "",
            },
          };
        }
      }

      return step;
    });

    if (JSON.stringify(nextSteps) !== JSON.stringify(falAiPromptSteps)) {
      setFieldValue("json.apresentation.configs.falai.prompt.steps", nextSteps);
    }
  }, [falAiPromptSteps, setFieldValue]);

  return (
    <>
      <Row className="mx-0 mb-3">
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Check
            checked={
              values.json.apresentation.configs.showLastUser === true
                ? true
                : false
            }
            type="checkbox"
            name="json.apresentation.configs.showLastUser"
            label="Adicionar botão para mostrar último QRCode gerado"
            onChange={(e) => {
              setFieldValue(
                "json.apresentation.configs.showLastUser",
                values.json.apresentation.configs.showLastUser === true
                  ? false
                  : true,
              );
            }}
          />

          <Form.Check
            checked={
              values.json.apresentation.configs.enableFrame === true
                ? true
                : false
            }
            type="checkbox"
            name="json.apresentation.configs.enableFrame"
            label="Ativar moldura (frame)"
            onChange={(e) => {
              setFieldValue(
                "json.apresentation.configs.enableFrame",
                e.target.checked,
              );
              if (!e.target.checked) {
                setFieldValue("json.apresentation.frame", "");
                setFieldValue("json.apresentation.frameHorizontal", "");
                setFieldValue(
                  "json.apresentation.configs.noFrameOnPreview",
                  false,
                );
              }
            }}
          />
          <Form.Check
            checked={
              values.json.apresentation.configs.noFrameOnPreview === true
            }
            type="checkbox"
            name="json.apresentation.configs.noFrameOnPreview"
            label="Remover frame da tela de preview"
            disabled={values.json.apresentation.configs.enableFrame !== true}
            onChange={(e) => {
              setFieldValue(
                "json.apresentation.configs.noFrameOnPreview",
                e.target.checked,
              );
            }}
          />
        </Form.Group>

        <p className="text-start fw-bold mt-3 mb-2">
          <FontAwesomeIcon icon={faGlobe} /> Tipo de mídia
        </p>
        <Form.Group as={Col} sm={12} className="mb-3">
          <Form.Check
            className="mode"
            checked={values.json.apresentation.configs.media === "image"}
            type="checkbox"
            name={"json.apresentation.configs.media"}
            onChange={(e) => {
              setFieldValue("json.apresentation.configs.media", "image");
              setFieldValue("json.apresentation.configs.falai.status", false);
            }}
            label={`Imagem`}
            isInvalid={!!errors.json?.apresentation?.configs?.media}
            feedback={errors.json?.apresentation?.configs?.media}
            feedbackType="invalid"
          ></Form.Check>

          <Form.Check
            className="mode"
            checked={values.json.apresentation.configs.media === "image-falai"}
            type="checkbox"
            name={"json.apresentation.configs.media"}
            onChange={(e) => {
              setFieldValue(
                "json.apresentation.configs.media",
                "image-falai",
              );
              setFieldValue("json.apresentation.configs.falai.status", true);
              if (!values.json.apresentation.configs.falai?.model) {
                setFieldValue(
                  "json.apresentation.configs.falai.model",
                  "fal-ai/nano-banana-pro/edit",
                );
              }
            }}
            label={`Imagem (Fal.ai)`}
            isInvalid={!!errors.json?.apresentation?.configs?.media}
            feedback={errors.json?.apresentation?.configs?.media}
            feedbackType="invalid"
          ></Form.Check>

          <Form.Check
            className="mode"
            checked={values.json.apresentation.configs.media === "video"}
            type="checkbox"
            name={"json.apresentation.configs.media"}
            onChange={(e) => {
              setFieldValue("json.apresentation.configs.media", "video");
              setFieldValue("json.apresentation.configs.falai.status", false);
              setFieldValue(
                "json.apresentation.configs.testImageUpload",
                false,
              );
              setFieldValue("json.apresentation.configs.printPhoto", false);
              setFieldValue(
                "json.apresentation.configs.printPhotoTray",
                false,
              );
            }}
            label={`Vídeo`}
            isInvalid={!!errors.json?.apresentation?.configs?.media}
            feedback={errors.json?.apresentation?.configs?.media}
            feedbackType="invalid"
          ></Form.Check>

          <Button
            type="button"
            variant="primary"
            size="sm"
            className="mt-2"
            onClick={openMediaConfigModal}
          >
            Configurar
          </Button>
        </Form.Group>

        <FalAiPromptGuideModal
          show={showFalAiPromptGuideModal && showMediaConfigModal}
          onHide={() => setShowFalAiPromptGuideModal(false)}
        />

        <Modal
          show={showMediaConfigModal}
          onHide={closeMediaConfigModal}
          size="xl"
          scrollable
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>Configurações do tipo de mídia</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mx-0 px-0">
        {values.json.apresentation.configs.media === "image" && (
          <Col sm={12}>
            <p className="opacity-75 mb-0">
              Nenhuma configuração adicional para imagem simples.
            </p>
          </Col>
        )}

        {values.json.apresentation.configs.media === "video" && (
          <Row className="mx-0 px-0 mb-3">
            <Form.Group as={Col} sm={6} className="mb-3">
              <Form.Label>Duração do vídeo (segundos)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={10}
                name="json.apresentation.configs.videoDuration"
                value={values.json.apresentation.configs.videoDuration || 5}
                onChange={(e) => {
                  let val = parseInt(e.target.value);
                  if (val > 10) val = 10;
                  if (val < 1) val = 1;
                  setFieldValue(
                    "json.apresentation.configs.videoDuration",
                    val,
                  );
                }}
                isInvalid={!!errors.json?.apresentation?.configs?.videoDuration}
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors.json?.apresentation?.configs?.videoDuration}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
        )}
        {/* Fal.ai Configuration Section */}
        {values.json.apresentation.configs.media === "image-falai" && (
          <Row className="mx-0 px-0 mb-3">
            <Form.Group as={Col} sm={12}>
              <Form.Label>Modelo</Form.Label>
              <Form.Select
                name="json.apresentation.configs.falai.model"
                value={
                  values.json.apresentation.configs.falai?.model ||
                  "fal-ai/nano-banana-pro/edit"
                }
                onChange={(event) => {
                  handleChange(event);
                  if (event.target.value === "fal-ai/nano-banana/edit") {
                    setFieldValue(
                      "json.apresentation.configs.falai.enable_web_search",
                      false,
                    );
                  }
                }}
                isInvalid={!!errors.json?.apresentation?.configs?.falai?.model}
                isValid={
                  touched.json?.apresentation?.configs?.falai?.model &&
                  !errors.json?.apresentation?.configs?.falai?.model
                }
              >
                <option value="fal-ai/nano-banana-pro/edit">
                  Nano Banana Pro (Edit)
                </option>
                <option value="fal-ai/nano-banana/edit">
                  Nano Banana (Edit)
                </option>
              </Form.Select>
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors.json?.apresentation?.configs?.falai?.model}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3 mt-3" as={Col} sm="12">
              <div className="d-flex justify-content-between align-items-end">
                <Form.Label className="mb-0">
                  Prompt
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      <div className="text-start">
                        <div>
                          Use o Prompt para montar a frase principal que o
                          fal.ai vai receber.
                        </div>
                        <div className="mt-2">
                          Tokens simples como {"{style}"} ou {" {environment}"}
                          inserem o valor escolhido em cada etapa. Em etapas
                          ocultas, o servidor sorteia uma opção e usa esse valor.
                        </div>
                        <div className="mt-2">
                          Tokens compostos como {" {style.campo1}"} ou
                          {" {style.campoMood}"} escolhem uma possibilidade do
                          bloco Elementos adicionais associada à opção usada na
                          etapa.
                        </div>
                        <div className="mt-2">
                          Em etapas do tipo Esconder etapa, o servidor sorteia
                          uma opção antes de resolver tanto o token simples
                          {" {style}"} quanto os tokens compostos como
                          {" {style.campo1}"}.
                        </div>
                        <div className="mt-2">
                          Exemplo: Retrato {"{style}"} com iluminacao
                          {" {style.campoLuz}"} em {" {environment}"}.
                        </div>
                      </div>
                    </Tooltip>
                  }
                >
                  <HelpTooltip icon={faExclamationCircle} />
                </OverlayTrigger>
                </Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={insertFalAiPromptVariable}
                >
                  + Adicionar variável
                </Button>
              </div>
              <div className="text-muted small mt-1">
                Descreva o resultado visual principal e use as variáveis para inserir as escolhas feitas no fluxo.
              </div>
              <Form.Control
                ref={falAiPromptBaseTextareaRef}
                as={"textarea"}
                className="mt-2"
                rows={3}
                name="json.apresentation.configs.falai.prompt.base"
                value={
                  values.json.apresentation.configs.falai?.prompt?.base || ""
                }
                onChange={(e) => {
                  rememberFalAiPromptBaseSelection(e);
                  updateFalAiPromptBase(e.target.value);
                }}
                onClick={rememberFalAiPromptBaseSelection}
                onKeyUp={rememberFalAiPromptBaseSelection}
                onSelect={rememberFalAiPromptBaseSelection}
                onFocus={rememberFalAiPromptBaseSelection}
                placeholder="Ex: Transforme esta foto em {style} em um cenário de {environment}"
                isInvalid={Boolean(falAiPromptBaseError)}
                isValid={
                  touched?.json?.apresentation?.configs?.falai?.prompt?.base &&
                  !falAiPromptBaseError
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {falAiPromptBaseError}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Prompt Steps Configuration */}
            {falAiPromptSteps.length > 0 && (
              <Form.Group as={Col} sm={12} className="mb-3">
                <p className="text-start fw-bold mb-2">
                  Configuração de cada página do fluxo do Photobooth
                </p>
                <Accordion
                  activeKey={activeFalAiStepAccordion}
                  onSelect={(eventKey) => setActiveFalAiStepAccordion(eventKey)}
                >
                  {falAiPromptSteps.map((step, stepIdx) => {
                    const normalizedStep = normalizeFalAiStepConfig(step);
                    const skipSourceSteps = falAiPromptSteps.filter(
                      (candidate, candidateIdx) =>
                        candidateIdx < stepIdx && candidate?.type !== "hidden",
                    );
                    const skipSourceStep = skipSourceSteps.find(
                      (candidate) => candidate.id === normalizedStep.skipIf?.stepId,
                    );
                    const stepMoreOptions = Array.isArray(normalizedStep.moreOptions)
                      ? normalizedStep.moreOptions
                      : [];
                    const stepMoreOptionErrors =
                      errors?.json?.apresentation?.configs?.falai?.prompt?.steps?.[
                        stepIdx
                      ]?.moreOptions;
                    const skipSourceUsesText = skipSourceStep?.type === "text";
                    const canConfigureSkipIf =
                      (normalizedStep.required !== false || normalizedStep.type === "hidden") &&
                      skipSourceSteps.length > 0;
                    const falAiOptionsHelpText =
                      normalizedStep.type === "hidden"
                        ? `Uma opção desta lista será sorteada no servidor para preencher {${normalizedStep.id}} e os tokens adicionais da etapa.`
                        : `Cada valor desta lista substitui {${normalizedStep.id}} no Prompt quando o usuário escolhe a opção.`;
                    const showFalAiMoreOptionsHelp =
                      normalizedStep.type === "hidden";
                    const falAiMoreOptionsExampleToken =
                      createFalAiNestedPlaceholderTemplate(
                        normalizedStep.id,
                        stepMoreOptions[0]?.label || "campo1",
                      );

                    return (
                      <Accordion.Item
                        eventKey={`${stepIdx}`}
                        key={`falai-step-${step.id || stepIdx}`}
                        className="mb-2 border rounded"
                      >
                        <Accordion.Header>
                          <div className="d-flex flex-column text-start">
                            <span className="fw-semibold">
                              {`Etapa ${stepIdx + 1}: {${step.id}}`}
                            </span>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>

                          <Row className="mb-3">
                            <Form.Group as={Col} md="6">
                              <Form.Label>Tipo</Form.Label>
                              <Form.Select
                                name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].type`}
                                value={normalizedStep.type || "select"}
                                onChange={(e) => {
                                  const nextType = e.target.value;
                                  const nextSteps = cloneFalAiValue(
                                    values.json.apresentation.configs.falai
                                      .prompt.steps,
                                  );

                                  nextSteps[stepIdx] = normalizeFalAiStepConfig({
                                    ...nextSteps[stepIdx],
                                    type: nextType,
                                    autoAdvance:
                                      nextType === "select"
                                        ? nextSteps?.[stepIdx]?.autoAdvance === true
                                        : false,
                                  });

                                  setFieldValue(
                                    "json.apresentation.configs.falai.prompt.steps",
                                    nextSteps,
                                  );
                                }}
                                isInvalid={
                                  !!errors?.json?.apresentation?.configs?.falai
                                    ?.prompt?.steps?.[stepIdx]?.type
                                }
                              >
                                <option value="select">Botões (Opções predefinidas)</option>
                                <option value="text">Texto Livre (Usuário digita)</option>
                                <option value="hidden">Esconder etapa (Insere automaticamente)</option>
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {
                                  errors?.json?.apresentation?.configs?.falai
                                    ?.prompt?.steps?.[stepIdx]?.type
                                }
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Row>
                          {normalizedStep.type !== "hidden" && (
                            <>
                              <Row className="mb-3">
                                <Form.Group>
                                  <Form.Label>Título/Pergunta</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].question`}
                                    value={normalizedStep.question}
                                    onChange={handleChange}
                                    placeholder="Ex: Que tipo de estilo você quer?"
                                    isInvalid={
                                      !!errors?.json?.apresentation?.configs?.falai
                                        ?.prompt?.steps?.[stepIdx]?.question
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {
                                      errors?.json?.apresentation?.configs?.falai
                                        ?.prompt?.steps?.[stepIdx]?.question
                                    }
                                  </Form.Control.Feedback>
                                </Form.Group>
                                {normalizedStep.type !== "select" && (
                                  <small className="text-muted d-block mt-1">
                                    {normalizedStep.type === "hidden"
                                      ? "Etapas ocultas não aparecem para o usuário. Uma das opções abaixo será sorteada no servidor."
                                      : "Etapas de texto usam botão ou Enter para continuar."}
                                  </small>
                                )}
                              </Row>
                              <Row className="mb-3">
                                <Form.Group as={Col} md="6">
                                  <Form.Check
                                    type="checkbox"
                                    name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].required`}
                                    label="Resposta obrigatória"
                                    checked={normalizedStep.required !== false}
                                    disabled={normalizedStep.type === "hidden"}
                                    onChange={(e) => {
                                      setFieldValue(
                                        `json.apresentation.configs.falai.prompt.steps[${stepIdx}].required`,
                                        e.target.checked,
                                      );

                                      if (!e.target.checked) {
                                        setFieldValue(
                                          `json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf`,
                                          null,
                                        );
                                      }
                                    }}
                                  />
                                  <Form.Check
                                    type="checkbox"
                                    name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].autoAdvance`}
                                    label="Avanço automático ao selecionar"
                                    checked={normalizedStep.autoAdvance === true}
                                    disabled={normalizedStep.type !== "select"}
                                    onChange={(e) => {
                                      setFieldValue(
                                        `json.apresentation.configs.falai.prompt.steps[${stepIdx}].autoAdvance`,
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                </Form.Group>
                              </Row>
                            </>
                          )}

                          {canConfigureSkipIf && (
                            <Row>
                              <Form.Group as={Col} md="6" className="mb-3">
                                <Form.Label>Pular esta etapa quando</Form.Label>
                                <Form.Select
                                  name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf.stepId`}
                                  value={normalizedStep.skipIf?.stepId || ""}
                                  onChange={(e) => {
                                    const selectedStepId = e.target.value;
                                    if (!selectedStepId) {
                                      setFieldValue(
                                        `json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf`,
                                        null,
                                      );
                                      return;
                                    }

                                    setFieldValue(
                                      `json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf`,
                                      {
                                        stepId: selectedStepId,
                                        value: "",
                                      },
                                    );
                                  }}
                                  isInvalid={
                                    !!errors?.json?.apresentation?.configs
                                      ?.falai?.prompt?.steps?.[stepIdx]?.skipIf
                                      ?.stepId
                                  }
                                >
                                  <option value="">Nunca pular</option>
                                  {skipSourceSteps.map((candidate) => (
                                    <option key={candidate.id} value={candidate.id}>
                                      {candidate.id} - {candidate.question || "Sem descrição"}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.skipIf?.stepId
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>

                              {normalizedStep.skipIf?.stepId && (
                                <Form.Group as={Col} md="6" className="mb-3">
                                  <Form.Label>Valor que dispara o pulo</Form.Label>
                                  {skipSourceUsesText ? (
                                    <Form.Control
                                      type="text"
                                      name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf.value`}
                                      value={normalizedStep.skipIf?.value || ""}
                                      onChange={handleChange}
                                      placeholder="Ex: não"
                                      isInvalid={
                                        !!errors?.json?.apresentation?.configs
                                          ?.falai?.prompt?.steps?.[stepIdx]
                                          ?.skipIf?.value
                                      }
                                    />
                                  ) : (
                                    <Form.Select
                                      name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf.value`}
                                      value={normalizedStep.skipIf?.value || ""}
                                      onChange={handleChange}
                                      isInvalid={
                                        !!errors?.json?.apresentation?.configs
                                          ?.falai?.prompt?.steps?.[stepIdx]
                                          ?.skipIf?.value
                                      }
                                    >
                                      <option value="">Selecione um valor</option>
                                      {(skipSourceStep?.options || []).map((option, optionIdx) => (
                                        <option
                                          key={
                                            getFalAiOptionId(option) ||
                                            `${skipSourceStep?.id || "step"}-${optionIdx}`
                                          }
                                          value={getFalAiOptionId(option)}
                                        >
                                          {getFalAiStepOptionLabel(option, optionIdx)}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  )}
                                  <Form.Control.Feedback type="invalid">
                                    {
                                      errors?.json?.apresentation?.configs
                                        ?.falai?.prompt?.steps?.[stepIdx]
                                        ?.skipIf?.value
                                    }
                                  </Form.Control.Feedback>
                                  <small className="text-muted d-block mt-1">
                                    Quando a etapa selecionada acima tiver este valor, esta etapa será ocultada.
                                  </small>
                                </Form.Group>
                              )}
                            </Row>
                          )}

                          {normalizedStep.type === "text" && (
                            <Row>
                              <Form.Group as={Col} md="6" className="mb-3">
                                <Form.Label>Placeholder</Form.Label>
                                <Form.Control
                                  type="text"
                                  name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].placeholder`}
                                  value={normalizedStep.placeholder || ""}
                                  onChange={handleChange}
                                  placeholder="Ex: Digite seu nome"
                                  isInvalid={
                                    !!errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.placeholder
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.placeholder
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                              <Form.Group as={Col} md="6" className="mb-3">
                                <Form.Label>Máximo de caracteres</Form.Label>
                                <Form.Control
                                  type="number"
                                  min={1}
                                  max={500}
                                  name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].maxLength`}
                                  value={normalizedStep.maxLength || 80}
                                  onChange={handleChange}
                                  isInvalid={
                                    !!errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.maxLength
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.maxLength
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Row>
                          )}
                          {canConfigureSkipIf && (
                            <Row>
                              <Form.Group as={Col} md="6" className="mb-3">
                                <Form.Label>Pular esta etapa quando</Form.Label>
                                <Form.Select
                                  name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf.stepId`}
                                  value={normalizedStep.skipIf?.stepId || ""}
                                  onChange={(e) => {
                                    const selectedStepId = e.target.value;
                                    if (!selectedStepId) {
                                      setFieldValue(
                                        `json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf`,
                                        null,
                                      );
                                      return;
                                    }

                                    setFieldValue(
                                      `json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf`,
                                      {
                                        stepId: selectedStepId,
                                        value: "",
                                      },
                                    );
                                  }}
                                  isInvalid={
                                    !!errors?.json?.apresentation?.configs
                                      ?.falai?.prompt?.steps?.[stepIdx]?.skipIf
                                      ?.stepId
                                  }
                                >
                                  <option value="">Nunca pular</option>
                                  {skipSourceSteps.map((candidate) => (
                                    <option key={candidate.id} value={candidate.id}>
                                      {candidate.id} - {candidate.question || "Sem descrição"}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.skipIf?.stepId
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>

                              {normalizedStep.skipIf?.stepId && (
                                <Form.Group as={Col} md="6" className="mb-3">
                                  <Form.Label>Valor que dispara o pulo</Form.Label>
                                  {skipSourceUsesText ? (
                                    <Form.Control
                                      type="text"
                                      name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf.value`}
                                      value={normalizedStep.skipIf?.value || ""}
                                      onChange={handleChange}
                                      placeholder="Ex: não"
                                      isInvalid={
                                        !!errors?.json?.apresentation?.configs
                                          ?.falai?.prompt?.steps?.[stepIdx]
                                          ?.skipIf?.value
                                      }
                                    />
                                  ) : (
                                    <Form.Select
                                      name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].skipIf.value`}
                                      value={normalizedStep.skipIf?.value || ""}
                                      onChange={handleChange}
                                      isInvalid={
                                        !!errors?.json?.apresentation?.configs
                                          ?.falai?.prompt?.steps?.[stepIdx]
                                          ?.skipIf?.value
                                      }
                                    >
                                      <option value="">Selecione um valor</option>
                                      {(skipSourceStep?.options || []).map((option, optionIdx) => (
                                        <option
                                          key={
                                            getFalAiOptionId(option) ||
                                            `${skipSourceStep?.id || "step"}-${optionIdx}`
                                          }
                                          value={getFalAiOptionId(option)}
                                        >
                                          {getFalAiStepOptionLabel(option, optionIdx)}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  )}
                                  <Form.Control.Feedback type="invalid">
                                    {
                                      errors?.json?.apresentation?.configs
                                        ?.falai?.prompt?.steps?.[stepIdx]
                                        ?.skipIf?.value
                                    }
                                  </Form.Control.Feedback>
                                  <small className="text-muted d-block mt-1">
                                    Quando a etapa selecionada acima tiver este valor, esta etapa será ocultada.
                                  </small>
                                </Form.Group>
                              )}
                            </Row>
                          )}

                          {normalizedStep.type === "text" && (
                            <Row>
                              <Form.Group as={Col} md="6" className="mb-3">
                                <Form.Label>Placeholder</Form.Label>
                                <Form.Control
                                  type="text"
                                  name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].placeholder`}
                                  value={normalizedStep.placeholder || ""}
                                  onChange={handleChange}
                                  placeholder="Ex: Digite seu nome"
                                  isInvalid={
                                    !!errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.placeholder
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.placeholder
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                              <Form.Group as={Col} md="6" className="mb-3">
                                <Form.Label>Máximo de caracteres</Form.Label>
                                <Form.Control
                                  type="number"
                                  min={1}
                                  max={500}
                                  name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].maxLength`}
                                  value={normalizedStep.maxLength || 80}
                                  onChange={handleChange}
                                  isInvalid={
                                    !!errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.maxLength
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors?.json?.apresentation?.configs?.falai
                                      ?.prompt?.steps?.[stepIdx]?.maxLength
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Row>
                          )}

                          {isFalAiOptionStep(normalizedStep) && (
                            <>
                              {normalizedStep.type === "select" && (
                                <Row className="mb-1">
                                  <Form.Group as={Col} md="3" className="mb-3">
                                    <Form.Label>Largura dos cards de imagem</Form.Label>
                                    <InputGroup>
                                      <Form.Control
                                        type="number"
                                        min={1}
                                        value={getFalAiStepSizeNumberValue(
                                          normalizedStep.optionImageWidth,
                                        )}
                                        onChange={(e) => {
                                          const nextValue = e.target.value;

                                          setFieldValue(
                                            `json.apresentation.configs.falai.prompt.steps[${stepIdx}].optionImageWidth`,
                                            nextValue ? `${nextValue}px` : "",
                                          );
                                        }}
                                        placeholder="220"
                                      />
                                      <InputGroup.Text>px</InputGroup.Text>
                                    </InputGroup>
                                  </Form.Group>
                                  <Form.Group as={Col} md="3" className="mb-3">
                                    <Form.Label>Altura dos cards de imagem</Form.Label>
                                    <InputGroup>
                                      <Form.Control
                                        type="number"
                                        min={1}
                                        value={getFalAiStepSizeNumberValue(
                                          normalizedStep.optionImageHeight,
                                        )}
                                        onChange={(e) => {
                                          const nextValue = e.target.value;

                                          setFieldValue(
                                            `json.apresentation.configs.falai.prompt.steps[${stepIdx}].optionImageHeight`,
                                            nextValue ? `${nextValue}px` : "",
                                          );
                                        }}
                                        placeholder="220"
                                      />
                                      <InputGroup.Text>px</InputGroup.Text>
                                    </InputGroup>
                                  </Form.Group>
                                </Row>
                              )}
                              <hr></hr>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                  <Form.Label className="mb-0">
                                    Opções
                                    <OverlayTrigger
                                      overlay={
                                        <Tooltip>
                                          <div className="text-start">
                                            {falAiOptionsHelpText}
                                          </div>
                                        </Tooltip>
                                      }
                                    >
                                      <HelpTooltip icon={faExclamationCircle} />
                                    </OverlayTrigger>
                                  </Form.Label>
                                  <div className="text-muted small mt-1">
                                    Cada opção define uma variação que o participante pode escolher e o valor que será usado no Prompt.
                                  </div>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {
                                    updateFalAiStepOptions(stepIdx, (currentOptions) => [
                                      ...currentOptions,
                                      createFalAiStepOptionTemplate(),
                                    ]);
                                  }}
                                >
                                  + Adicionar Opção
                                </Button>
                              </div>

                              {normalizedStep.options?.length > 0 ? (
                                <Row>
                                  {(normalizedStep.options || []).map((option, optIdx) => (
                                    <Form.Group
                                      as={Col}
                                      md="6"
                                      key={getFalAiOptionId(option) || optIdx}
                                      className="mb-3"
                                    >
                                      <Card className="position-relative">
                                        {normalizedStep.options.length > 1 && (
                                          <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute"
                                            style={{
                                              top: "7px",
                                              right: "7px",
                                              zIndex: 1,
                                            }}
                                            onClick={() => {
                                              updateFalAiStepOptions(
                                                stepIdx,
                                                (currentOptions) => {
                                                  const nextOptions = [...currentOptions];

                                                  nextOptions.splice(optIdx, 1);
                                                  return nextOptions;
                                                },
                                              );
                                            }}
                                          >
                                            <FontAwesomeIcon icon={faClose} />
                                          </Button>
                                        )}
                                        <Card.Body>
                                          <Form.Control
                                            type="hidden"
                                            name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].options[${optIdx}].id`}
                                            value={getFalAiOptionId(option)}
                                          />
                                          {normalizedStep.type !== "hidden" && (
                                            <Form.Group className="mb-2">
                                              <Form.Label>
                                                Descrição (exibido para o usuário)
                                              </Form.Label>
                                              <Form.Control
                                                type="text"
                                                name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].options[${optIdx}].label`}
                                                value={option.label}
                                                onChange={handleChange}
                                                placeholder="Ex: Super-Herói"
                                                isInvalid={
                                                  !!errors?.json?.apresentation?.configs
                                                    ?.falai?.prompt?.steps?.[stepIdx]
                                                    ?.options?.[optIdx]?.label
                                                }
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                {
                                                  errors?.json?.apresentation?.configs
                                                    ?.falai?.prompt?.steps?.[stepIdx]
                                                    ?.options?.[optIdx]?.label
                                                }
                                              </Form.Control.Feedback>
                                            </Form.Group>
                                          )}
                                          <Form.Group className="mb-2">
                                            <Form.Label>
                                              Valor (usado no prompt)
                                            </Form.Label>
                                            <Form.Control
                                              as="textarea"
                                              rows={2}
                                              name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].options[${optIdx}].value`}
                                              value={option.value}
                                              onChange={handleChange}
                                              placeholder="Ex: super-herói banana com capa voadora"
                                              isInvalid={
                                                !!errors?.json?.apresentation?.configs
                                                  ?.falai?.prompt?.steps?.[stepIdx]
                                                  ?.options?.[optIdx]?.value
                                              }
                                            />
                                            <Form.Control.Feedback type="invalid">
                                              {
                                                errors?.json?.apresentation?.configs
                                                  ?.falai?.prompt?.steps?.[stepIdx]
                                                  ?.options?.[optIdx]?.value
                                              }
                                            </Form.Control.Feedback>
                                          </Form.Group>
                                          <Form.Group className="mb-2">
                                            <Form.Label>Imagem (opcional)</Form.Label>
                                            <Form.Control
                                              type="hidden"
                                              name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].options[${optIdx}].image`}
                                              value={option.image}
                                            />
                                            <div className="imagesUpload mb-2">
                                              <img
                                                className="h-100 img-thumbnail"
                                                style={{
                                                  maxHeight: "150px",
                                                  objectFit: "cover",
                                                }}
                                                src={
                                                  option.image !== "" &&
                                                    option.image !== null
                                                    ? option.image.startsWith("http")
                                                      ? option.image
                                                      : domainConfig.imageServer +
                                                      "/customizacao/" +
                                                      values.customizacao_id +
                                                      "/" +
                                                      option.image
                                                    : domainConfig.imageServer +
                                                    "/empty-background.png"
                                                }
                                                alt="Option"
                                              />
                                            </div>
                                            <DropzoneComponent
                                              className="dropzoneComp"
                                              type="image"
                                              handlePreviewSuccess={
                                                handlePreviewSuccess
                                              }
                                              handlePreviewError={handlePreviewError}
                                              setFieldValue={setFieldValue}
                                              folder="customizacao"
                                              id={values.customizacao_id}
                                              field={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].options[${optIdx}].image`}
                                            />
                                            {hasFalAiImageReference(option?.image) && (
                                              <div className="mt-3">
                                                <Form.Check
                                                  type="checkbox"
                                                  id={`falai-use-as-ref-${stepIdx}-${optIdx}`}
                                                  name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].options[${optIdx}].useAsReferenceImage`}
                                                  label="Enviar imagem como referência para a IA"
                                                  checked={option.useAsReferenceImage !== false}
                                                  onChange={(e) => {
                                                    setFieldValue(
                                                      `json.apresentation.configs.falai.prompt.steps[${stepIdx}].options[${optIdx}].useAsReferenceImage`,
                                                      e.target.checked
                                                    );
                                                  }}
                                                />
                                                <Form.Text className="text-muted d-block small">
                                                  Se desmarcado, a imagem será usada apenas como miniatura/thumbnail no totem, sem ser enviada no prompt/image_urls da IA.
                                                </Form.Text>
                                              </div>
                                            )}
                                          </Form.Group>
                                        </Card.Body>
                                      </Card>
                                    </Form.Group>
                                  ))}
                                </Row>
                              ) : (
                                <p className="text-muted mb-0">
                                  {`Nenhuma opção disponível`}
                                </p>
                              )}
                              {typeof errors?.json?.apresentation?.configs?.falai
                                ?.prompt?.steps?.[stepIdx]?.options === "string" && (
                                  <div
                                    className="invalid-feedback d-block"
                                    data-error-path={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].options`}
                                  >
                                    {
                                      errors?.json?.apresentation?.configs?.falai
                                        ?.prompt?.steps?.[stepIdx]?.options
                                    }
                                  </div>
                                )}
                            </>
                          )}

                          {isFalAiOptionStep(normalizedStep) && (
                            <>
                              <hr></hr>
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div>
                                  <Form.Label className="mb-0">
                                    Elementos adicionais
                                    {showFalAiMoreOptionsHelp && (
                                      <OverlayTrigger
                                        overlay={
                                          <Tooltip>
                                            <div className="text-start">
                                              Esta etapa não aparece para o usuário. O servidor
                                              sorteia uma opção para preencher tanto {" "}
                                              {createFalAiStepPlaceholderTemplate(
                                                normalizedStep.id,
                                              )}{" "}
                                              quanto tokens adicionais como {" "}
                                              {falAiMoreOptionsExampleToken}{" "}
                                              usando os valores associados à opção escolhida.
                                            </div>
                                          </Tooltip>
                                        }
                                      >
                                        <HelpTooltip icon={faExclamationCircle} />
                                      </OverlayTrigger>
                                    )}
                                  </Form.Label>
                                  <div className="text-muted small mt-1">
                                    Valores complementares associados a cada opção para preencher tokens adicionais do Prompt.
                                  </div>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    handleAddFalAiMoreOption(
                                      normalizedStep.id,
                                      stepMoreOptions,
                                    )
                                  }
                                >
                                  + Adicionar elemento
                                </Button>
                              </div>

                              {stepMoreOptions.length > 0 ? (
                                <Row>
                                  {stepMoreOptions.map((option, optionIdx) => {
                                    const optionErrors = Array.isArray(stepMoreOptionErrors)
                                      ? stepMoreOptionErrors?.[optionIdx]
                                      : undefined;
                                    const optionToken = createFalAiNestedPlaceholderTemplate(
                                      normalizedStep.id,
                                      option.label || "",
                                    );

                                    return (
                                      <Form.Group
                                        as={Col}
                                        md="6"
                                        className="mb-3"
                                        key={`${normalizedStep.id}-${option.label || optionIdx}`}
                                      >
                                        <Card className="h-100 position-relative">
                                          <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute"
                                            style={{
                                              top: "7px",
                                              right: "7px",
                                              zIndex: 1,
                                            }}
                                            onClick={() => {
                                              handleRemoveFalAiMoreOption(
                                                normalizedStep.id,
                                                option.label,
                                              );
                                            }}
                                          >
                                            <FontAwesomeIcon icon={faClose} />
                                          </Button>
                                          <Card.Body>
                                            <Form.Group className="mb-3">
                                              <Form.Label>Placeholder</Form.Label>
                                              <Form.Control
                                                type="text"
                                                name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].moreOptions[${optionIdx}].label`}
                                                value={optionToken}
                                                onChange={(e) => {
                                                  handleFalAiMoreOptionLabelChange(
                                                    stepIdx,
                                                    optionIdx,
                                                    getFalAiMoreOptionLabelFromTokenInput(
                                                      normalizedStep.id,
                                                      e.target.value,
                                                    ),
                                                  );
                                                }}
                                                placeholder={createFalAiNestedPlaceholderTemplate(
                                                  normalizedStep.id,
                                                  "campo1",
                                                )}
                                                isInvalid={!!optionErrors?.label}
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                {optionErrors?.label}
                                              </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-2">
                                              <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Form.Label className="mb-0">
                                                  Possibilidades por opção
                                                </Form.Label>
                                              </div>

                                              {(normalizedStep.options || []).map(
                                                (stepOption, stepOptionIdx) => {
                                                  const optionId = getFalAiOptionId(stepOption);
                                                  const possibilityIdx = (
                                                    option.possibilities || []
                                                  ).findIndex(
                                                    (possibility) =>
                                                      getFalAiMoreOptionPossibilityId(
                                                        possibility,
                                                      ) === optionId,
                                                  );
                                                  const possibility =
                                                    possibilityIdx >= 0
                                                      ? option.possibilities?.[
                                                      possibilityIdx
                                                      ]
                                                      : {
                                                        id: optionId,
                                                        values: [""],
                                                      };
                                                  const possibilityValues =
                                                    getFalAiMoreOptionPossibilityValues(
                                                      possibility,
                                                    );
                                                  const possibilityError =
                                                    Array.isArray(
                                                      optionErrors?.possibilities,
                                                    )
                                                      ? optionErrors?.possibilities?.[
                                                      possibilityIdx
                                                      ]
                                                      : undefined;

                                                  return (
                                                    <div
                                                      className="mb-3"
                                                      key={`${normalizedStep.id}-${option.label || optionIdx}-${optionId || stepOptionIdx}`}
                                                    >
                                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <small className="fw-semibold text-muted">
                                                          {getFalAiStepOptionLabel(
                                                            stepOption,
                                                            stepOptionIdx,
                                                          )}
                                                        </small>
                                                        <Button
                                                          variant="primary"
                                                          size="sm"
                                                          onClick={() => {
                                                            updateFalAiMoreOptionPossibilityValues(
                                                              stepIdx,
                                                              optionIdx,
                                                              normalizedStep.options || [],
                                                              optionId,
                                                              (currentValues) => [
                                                                ...currentValues,
                                                                "",
                                                              ],
                                                            );
                                                          }}
                                                        >
                                                          +
                                                        </Button>
                                                      </div>

                                                      {possibilityValues.map(
                                                        (
                                                          possibilityValue,
                                                          possibilityValueIdx,
                                                        ) => {
                                                          const possibilityValueError =
                                                            Array.isArray(
                                                              possibilityError?.values,
                                                            )
                                                              ? possibilityError?.values?.[
                                                              possibilityValueIdx
                                                              ]
                                                              : undefined;

                                                          return (
                                                            <div
                                                              className="mb-2"
                                                              key={`${normalizedStep.id}-${option.label || optionIdx}-${optionId || stepOptionIdx}-${possibilityValueIdx}`}
                                                            >
                                                              <InputGroup hasValidation>
                                                                <Form.Control
                                                                  type="text"
                                                                  name={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].moreOptions[${optionIdx}].possibilities[${Math.max(possibilityIdx, 0)}].values[${possibilityValueIdx}]`}
                                                                  value={
                                                                    possibilityValue ?? ""
                                                                  }
                                                                  onChange={(e) => {
                                                                    updateFalAiMoreOptionPossibilityValues(
                                                                      stepIdx,
                                                                      optionIdx,
                                                                      normalizedStep.options || [],
                                                                      optionId,
                                                                      (
                                                                        currentValues,
                                                                      ) => {
                                                                        const nextValues = [
                                                                          ...currentValues,
                                                                        ];

                                                                        nextValues[
                                                                          possibilityValueIdx
                                                                        ] =
                                                                          e.target.value;
                                                                        return nextValues;
                                                                      },
                                                                    );
                                                                  }}
                                                                  placeholder="Ex: neon ou dramático"
                                                                  isInvalid={
                                                                    !!possibilityValueError
                                                                  }
                                                                />
                                                                {possibilityValues.length > 1 && (
                                                                  <Button
                                                                    variant="outline-danger"
                                                                    onClick={() => {
                                                                      updateFalAiMoreOptionPossibilityValues(
                                                                        stepIdx,
                                                                        optionIdx,
                                                                        normalizedStep.options || [],
                                                                        optionId,
                                                                        (
                                                                          currentValues,
                                                                        ) => {
                                                                          const nextValues = [
                                                                            ...currentValues,
                                                                          ];

                                                                          nextValues.splice(
                                                                            possibilityValueIdx,
                                                                            1,
                                                                          );
                                                                          return nextValues;
                                                                        },
                                                                      );
                                                                    }}
                                                                  >
                                                                    <FontAwesomeIcon icon={faClose} />
                                                                  </Button>
                                                                )}
                                                                <Form.Control.Feedback type="invalid">
                                                                  {
                                                                    possibilityValueError
                                                                  }
                                                                </Form.Control.Feedback>
                                                              </InputGroup>
                                                            </div>
                                                          );
                                                        },
                                                      )}

                                                      {typeof possibilityError?.values === "string" && (
                                                        <div
                                                          className="invalid-feedback d-block"
                                                          data-error-path={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].moreOptions[${optionIdx}].possibilities[${Math.max(possibilityIdx, 0)}].values`}
                                                        >
                                                          {possibilityError?.values}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                },
                                              )}

                                              {typeof optionErrors?.possibilities === "string" && (
                                                <div
                                                  className="invalid-feedback d-block"
                                                  data-error-path={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].moreOptions[${optionIdx}].possibilities`}
                                                >
                                                  {optionErrors?.possibilities}
                                                </div>
                                              )}
                                            </Form.Group>
                                          </Card.Body>
                                        </Card>
                                      </Form.Group>
                                    );
                                  })}
                                </Row>
                              ) : (
                                <p className="text-muted mb-0">
                                  Nenhum elemento adicional configurado para esta etapa.
                                </p>
                              )}

                              {typeof stepMoreOptionErrors === "string" && (
                                <div
                                  className="invalid-feedback d-block"
                                  data-error-path={`json.apresentation.configs.falai.prompt.steps[${stepIdx}].moreOptions`}
                                >
                                  {stepMoreOptionErrors}
                                </div>
                              )}
                            </>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </Form.Group>
            )}

            <Form.Group as={Col} sm={12} className="mb-3">
              <div className="d-flex justify-content-between align-items-end">
                <Form.Label className="mb-0">
                  Imagens de referência
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        As imagens de referência são enviadas ao fal.ai logo
                        depois da foto do participante. Elas não adicionam texto
                        ao Prompt e a ordem, sozinha, não define o papel visual.
                        Descreva no Prompt como cada referência deve ser usada:
                        cenário, figurino, pose, composição ou estilo.
                      </Tooltip>
                    }
                  >
                    <HelpTooltip icon={faExclamationCircle} />
                  </OverlayTrigger>
                </Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const newItems = [
                      ...(values.json.apresentation.configs.falai
                        .extraReferences || []),
                    ];
                    newItems.push({ image: "" });
                    setFieldValue(
                      "json.apresentation.configs.falai.extraReferences",
                      newItems,
                    );
                  }}
                >
                  + Adicionar imagem
                </Button>
              </div>
              {values.json.apresentation.configs.falai?.extraReferences.length ===
                0 ? (
                <p className="opacity-75 mb-0 mt-2">
                  (Nenhuma imagem de referência adicionada)
                </p>
              ) : (
                <Accordion
                  activeKey={activeFalAiReferenceAccordion}
                  onSelect={(eventKey) => setActiveFalAiReferenceAccordion(eventKey)}
                  className="mt-3"
                >
                  {values.json.apresentation.configs.falai?.extraReferences?.map(
                    (item, idx) => {
                      return (
                        <Accordion.Item
                          eventKey={`${idx}`}
                          key={`extra-reference-${idx}`}
                          className="mb-2 border rounded"
                        >
                          <Accordion.Header>
                            <div className="d-flex flex-column text-start">
                              <span className="fw-semibold">{`Referência ${idx + 1}`}</span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <div className="d-flex justify-content-end mb-3">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                  const newItems = [
                                    ...(values.json.apresentation.configs.falai
                                      .extraReferences || []),
                                  ];
                                  newItems.splice(idx, 1);
                                  setFieldValue(
                                    "json.apresentation.configs.falai.extraReferences",
                                    newItems,
                                  );
                                }}
                              >
                                <FontAwesomeIcon icon={faClose} />
                              </Button>
                            </div>
                            <Form.Group className="mb-2">
                              <Form.Label>Imagem</Form.Label>
                              <div className="imagesUpload mb-2">
                                <img
                                  className="h-100 img-thumbnail"
                                  style={{
                                    maxHeight: "150px",
                                    objectFit: "cover",
                                  }}
                                  src={
                                    item.image && item.image !== ""
                                      ? item.image.startsWith("http")
                                        ? item.image
                                        : domainConfig.imageServer +
                                        "/customizacao/" +
                                        values.customizacao_id +
                                        "/" +
                                        item.image
                                      : domainConfig.imageServer +
                                      "/empty-background.png"
                                  }
                                  alt="Reference"
                                />
                              </div>
                              <DropzoneComponent
                                className="dropzoneComp"
                                type="image"
                                handlePreviewSuccess={handlePreviewSuccess}
                                handlePreviewError={handlePreviewError}
                                setFieldValue={setFieldValue}
                                folder="customizacao"
                                id={values.customizacao_id}
                                field={`json.apresentation.configs.falai.extraReferences[${idx}].image`}
                              />
                            </Form.Group>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    },
                  )}
                </Accordion>
              )}
            </Form.Group>

            <Form.Group as={Col} sm={3} className="mb-3">
              <Form.Label>Aspect Ratio</Form.Label>
              <Form.Select
                name="json.apresentation.configs.falai.aspect_ratio"
                value={
                  values.json.apresentation.configs.falai?.aspect_ratio ||
                  "auto"
                }
                onChange={handleChange}
              >
                <option value="auto">Auto</option>
                <option value="21:9">21:9</option>
                <option value="16:9">16:9</option>
                <option value="3:2">3:2</option>
                <option value="4:3">4:3</option>
                <option value="5:4">5:4</option>
                <option value="1:1">1:1</option>
                <option value="4:5">4:5</option>
                <option value="3:4">3:4</option>
                <option value="2:3">2:3</option>
                <option value="9:16">9:16</option>
              </Form.Select>
            </Form.Group>

            <Form.Group as={Col} sm={3} className="mb-3">
              <Form.Label>Formato de saída</Form.Label>
              <Form.Select
                name="json.apresentation.configs.falai.output_format"
                value={
                  values.json.apresentation.configs.falai?.output_format ||
                  "png"
                }
                onChange={handleChange}
              >
                <option value="png">PNG</option>
                <option value="jpeg">JPEG</option>
                <option value="webp">WebP</option>
              </Form.Select>
            </Form.Group>

            {/* Resolution - Only for Nano Banana Pro */}
            {values.json.apresentation.configs.falai?.model ===
              "fal-ai/nano-banana-pro/edit" && (
                <Form.Group as={Col} sm={3} className="mb-3">
                  <Form.Label>Resolução</Form.Label>
                  <Form.Select
                    name="json.apresentation.configs.falai.resolution"
                    value={
                      values.json.apresentation.configs.falai?.resolution || "1K"
                    }
                    onChange={handleChange}
                  >
                    <option value="1K">1K</option>
                    <option value="2K">2K</option>
                    <option value="4K">4K</option>
                  </Form.Select>
                </Form.Group>
              )}

            <Form.Group as={Col} sm={3} className="mb-3">
              <Form.Label>Total de imagens geradas</Form.Label>
              <Form.Control
                type="number"
                name="json.apresentation.configs.falai.num_images"
                value={values.json.apresentation.configs.falai?.num_images || 1}
                onChange={handleChange}
                min={1}
                max={4}
                isInvalid={
                  !!errors.json?.apresentation?.configs?.falai?.num_images
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors.json?.apresentation?.configs?.falai?.num_images}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} sm={12} className="mb-3">
              <Form.Check
                checked={
                  values.json.apresentation.configs.falai
                    ?.limit_generations === true
                }
                type="checkbox"
                label={
                  <>
                    Limitar gerações
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          Disponível nos dois modelos Nano Banana. Limita cada
                          rodada interna a uma geração e ignora instruções do
                          prompt sobre quantidade de imagens. O campo Total de
                          imagens geradas continua controlando a saída.
                        </Tooltip>
                      }
                    >
                      <HelpTooltip icon={faExclamationCircle} />
                    </OverlayTrigger>
                  </>
                }
                onChange={(e) => {
                  setFieldValue(
                    "json.apresentation.configs.falai.limit_generations",
                    e.target.checked,
                  );
                }}
              />

              {values.json.apresentation.configs.falai?.model ===
                "fal-ai/nano-banana-pro/edit" && (
                  <Form.Check
                    checked={
                      values.json.apresentation.configs.falai
                        ?.enable_web_search === true
                    }
                    type="checkbox"
                    label={
                      <>
                        Ativar busca na web (web search)
                        <OverlayTrigger
                          overlay={
                            <Tooltip>
                              Disponível apenas no Nano Banana Pro. Pode ajudar
                              quando o resultado depende de fatos atuais,
                              lugares ou marcas reais, mas tende a aumentar
                              variação, custo e tempo.
                            </Tooltip>
                          }
                        >
                          <HelpTooltip icon={faExclamationCircle} />
                        </OverlayTrigger>
                      </>
                    }
                    onChange={(e) => {
                      setFieldValue(
                        "json.apresentation.configs.falai.enable_web_search",
                        e.target.checked,
                      );
                    }}
                  />

                )}
            </Form.Group>

            <Form.Group as={Col} sm={12}>
              <div className="mb-2">
                <Form.Label className="fw-bold mb-0">
                  Revisão do prompt
                </Form.Label>
              </div>
              {[
                {
                  title: "PROMPT CONFIGURADO",
                  content: falAiPromptPreview.configuredPrompt,
                },
                {
                  title: "EXEMPLO DE SAÍDA",
                  content: falAiPromptPreview.previewOutput,
                },
                {
                  title: "DETALHES E POSSIBILIDADES",
                  content: falAiPromptPreview.details,
                },
              ].map((section) => (
                <Card
                  className="border bg-light mb-3"
                  key={`falai-prompt-review-${section.title}`}
                >
                  <Card.Body>
                    <p className="fw-bold mb-2">{section.title}</p>
                    {section.title === "EXEMPLO DE SAÍDA" &&
                      falAiPromptPreview.optionControls.length > 0 && (
                        <div className="mb-3">
                          <div className="text-muted small mb-2">
                            Selecione as opções para simular o Prompt final enviado ao fal.ai.
                            Essas escolhas não alteram o fluxo salvo do participante.
                          </div>
                          <Row>
                            {falAiPromptPreview.optionControls.map((control) => (
                              <Form.Group
                                as={Col}
                                md={6}
                                key={`falai-preview-option-${control.id}`}
                                className="mb-3"
                              >
                                <Form.Label>{control.label}</Form.Label>
                                <Form.Select
                                  value={control.selectedValue}
                                  disabled={control.isSkipped}
                                  onChange={(event) =>
                                    updateFalAiPreviewSelection(
                                      control.id,
                                      event.target.value,
                                    )
                                  }
                                >
                                  {!control.required && (
                                    <option value="">
                                      Sem seleção (remover do Prompt)
                                    </option>
                                  )}
                                  {control.options.map((option, optionIdx) => (
                                    <option
                                      key={
                                        getFalAiOptionId(option) ||
                                        `${control.id}-${optionIdx}`
                                      }
                                      value={getFalAiOptionId(option)}
                                    >
                                      {getFalAiStepOptionLabel(option, optionIdx)}
                                    </option>
                                  ))}
                                </Form.Select>
                                {control.isSkipped && (
                                  <Form.Text className="text-muted">
                                    Esta etapa está ignorada pela regra de pulo atual.
                                  </Form.Text>
                                )}
                              </Form.Group>
                            ))}
                          </Row>
                        </div>
                      )}
                    <pre
                      className="mb-0 small"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "monospace",
                      }}
                    >
                      {section.content}
                    </pre>
                    {section.title === "EXEMPLO DE SAÍDA" && (
                      <div className="d-flex justify-content-start mt-3">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={openFalAiPromptChatModal}
                          disabled={!canAnalyzeFalAiPrompt}
                        >
                          Analisar prompt
                        </Button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
              {falAiPromptFeedbackError && (
                <div className="text-danger small mt-2">
                  {falAiPromptFeedbackError}
                </div>
              )}
              {falAiPromptFeedback && (
                <Card className="border mt-3">
                  <Card.Body>
                    {hasAppliedFalAiPromptFeedback ? (
                      <div className="p-5 text-center">
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="text-success mb-3"
                          style={{ fontSize: "3.5rem" }}
                        />
                        <p className="fw-bold mb-1">Configuração aplicada</p>
                        <p className="text-muted small mb-3">
                          A configuração foi atualizada. Analise novamente para
                          calcular a pontuação dos novos valores.
                        </p>
                        <span className="badge bg-secondary text-white">
                          Nova pontuação pendente
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <p className="fw-bold mb-1">Análise do prompt</p>
                            {falAiPromptFeedback.summary && (
                              <p className="mb-0 small">
                                {falAiPromptFeedback.summary}
                              </p>
                            )}
                          </div>
                          {falAiPromptFeedback.score !== null &&
                            falAiPromptFeedback.score !== undefined && (
                              <div className="text-end ms-3">
                                <span className="badge bg-primary text-black">
                                  {falAiPromptFeedback.score}/100
                                </span>
                                {falAiPromptFeedback.scoreLabel && (
                                  <div className="text-muted small mt-1 text-nowrap">
                                    {falAiPromptFeedback.scoreLabel}
                                  </div>
                                )}
                              </div>
                          )}
                        </div>

                        <FalAiPromptScoreBreakdown
                          criteria={falAiPromptFeedback.scoreBreakdown}
                        />

                        {falAiPromptFeedback.scoreComplete === false && (
                          <div className="text-warning small mt-3">
                            A IA retornou uma pontuação incompleta. Execute a análise
                            novamente antes de aplicar sugestões.
                          </div>
                        )}

                        {falAiPromptFeedbackWarnings.length > 0 && (
                          <div className="mt-3">
                            <p className="fw-semibold mb-1 small">Avisos</p>
                            <ul className="mb-0 small">
                              {falAiPromptFeedbackWarnings.map((warning, idx) => (
                                <li key={`falai-feedback-warning-${idx}`}>
                                  {warning}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {falAiPromptFeedbackSuggestions.length > 0 && (
                          <div className="mt-3">
                            <p className="fw-semibold mb-1 small">Sugestões</p>
                            <ul className="mb-0 small">
                              {falAiPromptFeedbackSuggestions.map((suggestion, idx) => (
                                <li key={`falai-feedback-suggestion-${idx}`}>
                                  {suggestion}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="mt-3">
                          <div className="d-flex justify-content-end">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleApplyFalAiPromptFeedback}
                              disabled={!canApplyFalAiPromptFeedback}
                              title={
                                canApplyFalAiPromptFeedback
                                  ? "Aplicar configuração otimizada"
                                  : `Aplicação indisponível: ${falAiPromptApplicationMessage ||
                                  "nenhuma alteração aplicável"
                                  }`
                              }
                            >
                              <FontAwesomeIcon icon={faCheck} className="me-1" />
                              Aplicar sugestões
                            </Button>
                          </div>
                          {!canApplyFalAiPromptFeedback &&
                            falAiPromptApplicationMessage && (
                              <div className="text-warning text-end small mt-1">
                                <span className="fw-semibold">
                                  Aplicação indisponível:
                                </span>{" "}
                                {falAiPromptApplicationMessage}
                              </div>
                            )}
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Form.Group>
          </Row>
        )}
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="secondary"
              onClick={closeMediaConfigModal}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={saveMediaConfigModal}
            >
              Salvar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showFalAiPromptChatModal}
          onHide={() => setShowFalAiPromptChatModal(false)}
          size="lg"
          centered
          scrollable
          backdrop="static"
          className="nested-modal"
          style={{ zIndex: 1070 }}
          backdropClassName="nested-modal-backdrop-lv2"
        >
          <Modal.Header closeButton>
            <Modal.Title>Analisar prompt</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!falAiPromptChatSubmittedPrompt && (
              <Form.Group>
                <Form.Label className="d-flex align-items-center mb-2">
                  <span>Prompt enviado ao modelo de análise</span>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        Edite o prompt antes de enviar. Cada envio inicia uma análise; Não existe continuação da conversa.
                      </Tooltip>
                    }
                  >
                    <HelpTooltip icon={faExclamationCircle} />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={9}
                  value={falAiPromptChatDraft}
                  onChange={(event) =>
                    updateFalAiPromptChatDraft(event.target.value)
                  }
                  disabled={
                    isFalAiPromptFeedbackLoading ||
                    isFalAiPromptFullPromptLoading
                  }
                  isInvalid={Boolean(falAiPromptChatError)}
                />
                {falAiPromptChatError && (
                  <Form.Control.Feedback type="invalid">
                    {falAiPromptChatError}
                  </Form.Control.Feedback>
                )}
                <a
                  href="#fal-ai-full-prompt"
                  className="d-inline-block small text-primary mt-2"
                  aria-disabled={
                    !canSubmitFalAiPromptChat ||
                    isFalAiPromptFeedbackLoading ||
                    isFalAiPromptFullPromptLoading
                  }
                  onClick={(event) => {
                    event.preventDefault();
                    if (
                      canSubmitFalAiPromptChat &&
                      !isFalAiPromptFeedbackLoading &&
                      !isFalAiPromptFullPromptLoading
                    ) {
                      toggleFalAiPromptFullPrompt();
                    }
                  }}
                >
                  {isFalAiPromptFullPromptLoading
                    ? "Carregando prompt completo..."
                    : showFalAiPromptFullPrompt
                      ? "Ocultar prompt completo"
                      : "Ver prompt completo da análise"}
                </a>
                {falAiPromptFullPromptError && (
                  <div className="text-danger small mt-1">
                    {falAiPromptFullPromptError}
                  </div>
                )}
                <Collapse in={showFalAiPromptFullPrompt}>
                  <div id="fal-ai-full-prompt">
                    {falAiPromptFullPrompt && (
                      <Card className="border bg-light mt-2">
                        <Card.Body>
                          <p className="fw-bold mb-2">
                            Prompt completo enviado ao modelo de análise
                          </p>
                          <pre
                            className="mb-0 small"
                            style={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontFamily: "monospace",
                            }}
                          >
                            {falAiPromptFullPrompt}
                          </pre>
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                </Collapse>
              </Form.Group>
            )}

            {falAiPromptChatSubmittedPrompt && (
              <>
                <Card className="border bg-light mb-3">
                  <Card.Body>
                    <p className="fw-bold mb-2">Você</p>
                    <pre
                      className="mb-0 small"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "monospace",
                      }}
                    >
                      {falAiPromptChatSubmittedPrompt}
                    </pre>
                  </Card.Body>
                </Card>

                {isFalAiPromptFeedbackLoading && (
                  <div className="text-muted small py-3">
                    O modelo está analisando o prompt...
                  </div>
                )}

                {falAiPromptFeedback && (
                  <Card className="border">
                    <Card.Body>
                      <p className="fw-bold mb-2">Modelo</p>
                      {falAiPromptFeedback.summary && (
                        <p className="mb-3">{falAiPromptFeedback.summary}</p>
                      )}

                      {falAiPromptFeedback.score !== null &&
                        falAiPromptFeedback.score !== undefined && (
                          <div className="mb-3">
                            <span className="badge bg-primary text-black">
                              {falAiPromptFeedback.score}/100
                            </span>
                            {falAiPromptFeedback.scoreLabel && (
                              <span className="text-muted small ms-2">
                                {falAiPromptFeedback.scoreLabel}
                              </span>
                            )}
                          </div>
                        )}

                      <FalAiPromptScoreBreakdown
                        criteria={falAiPromptFeedback.scoreBreakdown}
                      />

                      {falAiPromptFeedbackWarnings.length > 0 && (
                        <div className="mb-3">
                          <p className="fw-semibold mb-1 small">Avisos</p>
                          <ul className="mb-0 small">
                            {falAiPromptFeedbackWarnings.map((warning, idx) => (
                              <li key={`falai-chat-warning-${idx}`}>
                                {warning}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {falAiPromptFeedbackSuggestions.length > 0 && (
                        <div className="mb-3">
                          <p className="fw-semibold mb-1 small">Sugestões</p>
                          <ul className="mb-0 small">
                            {falAiPromptFeedbackSuggestions.map((suggestion, idx) => (
                              <li key={`falai-chat-suggestion-${idx}`}>
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {falAiPromptFeedback.rewrittenPrompt && (
                        <div>
                          <p className="fw-semibold mb-1 small">
                            Prompt sugerido
                          </p>
                          <pre
                            className="mb-0 small"
                            style={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontFamily: "monospace",
                            }}
                          >
                            {falAiPromptFeedback.rewrittenPrompt}
                          </pre>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </>
            )}

            {falAiPromptFeedbackError && !falAiPromptChatSubmittedPrompt && (
              <div className="text-danger small mt-3">
                {falAiPromptFeedbackError}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {falAiPromptChatSubmittedPrompt ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowFalAiPromptChatModal(false)}
                >
                  Fechar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={resetFalAiPromptChat}
                  disabled={isFalAiPromptFeedbackLoading}
                >
                  Resetar e editar
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowFalAiPromptChatModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleFalAiPromptFeedback}
                  disabled={!canSubmitFalAiPromptChat}
                >
                  {isFalAiPromptFeedbackLoading
                    ? "Analisando..."
                    : "Enviar para análise"}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>

        <p className="text-start fw-bold mt-3 mb-2">Formulário</p>
        <Form.Group as={Col} sm={12} className="mb-3">
          <Form.Check
            className="mode"
            checked={
              values.json.apresentation.configs.form === false ? true : false
            }
            type="checkbox"
            name={"json.apresentation.configs.form"}
            onChange={(e) => {
              setFieldValue("json.apresentation.configs.form", false);
            }}
            label={`Sem coleta`}
            isInvalid={!!errors.json?.apresentation?.configs?.form}
            feedback={errors.json?.apresentation?.configs?.form}
            feedbackType="invalid"
          ></Form.Check>
          <Form.Check
            className="mode"
            checked={
              values.json.apresentation.configs.form === 1 ? true : false
            }
            type="checkbox"
            name={"json.apresentation.configs.form"}
            onChange={(e) => {
              setFieldValue("json.apresentation.configs.form", 1);
            }}
            label="Durante o processo de captura"
            isInvalid={!!errors.json?.apresentation?.configs?.form}
            feedback={errors.json?.apresentation?.configs?.form}
            feedbackType="invalid"
          ></Form.Check>
          <Form.Check
            className="mode"
            checked={
              values.json.apresentation.configs.form === 2 ? true : false
            }
            type="checkbox"
            name={"json.apresentation.configs.form"}
            onChange={(e) => {
              setFieldValue("json.apresentation.configs.form", 2);
            }}
            label="Registro feito no fluxo do usuário após leitura do Código QR"
            isInvalid={!!errors.json?.apresentation?.configs?.form}
            feedback={errors.json?.apresentation?.configs?.form}
            feedbackType="invalid"
          ></Form.Check>
        </Form.Group>

        {values.json.apresentation.configs.media !== "video" && (
          <>
            <p className="text-start fw-bold mt-3 mb-2">Impressão</p>
            <Form.Group as={Col} sm="12" className="mb-3">
              <Form.Check
                type="radio"
                name="printMode"
                id="printModeNone"
                label="Desativado"
                checked={
                  !values.json.apresentation.configs.printPhoto &&
                  !values.json.apresentation.configs.printPhotoTray
                }
                onChange={() => {
                  setFieldValue("json.apresentation.configs.printPhoto", false);
                  setFieldValue(
                    "json.apresentation.configs.printPhotoTray",
                    false,
                  );
                }}
              />
              <Form.Check
                type="radio"
                name="printMode"
                id="printPhoto"
                label="Ativar impressão via Browser"
                checked={values.json.apresentation.configs.printPhoto}
                onChange={() => {
                  setFieldValue("json.apresentation.configs.printPhoto", true);
                  setFieldValue(
                    "json.apresentation.configs.printPhotoTray",
                    false,
                  );
                }}
              />
              <Form.Check
                type="radio"
                name="printMode"
                id="printPhotoTray"
                label="Ativar impressão via QZ Tray"
                checked={values.json.apresentation.configs.printPhotoTray}
                onChange={() => {
                  setFieldValue(
                    "json.apresentation.configs.printPhotoTray",
                    true,
                  );
                  setFieldValue("json.apresentation.configs.printPhoto", false);
                }}
              />
            </Form.Group>
          </>
        )}
      </Row>
      {values.json.apresentation.configs.enableFrame === true && (
        <>
          <Row className="mx-0 mb-3">
            <p className="text-start fw-bold mb-2">
              Media <span className="small">(Tamanho máximo: 10mb)</span>
            </p>
            <Form.Group as={Col} md="6" className="mb-3">
              <Card className="mb-3">
                <Card.Body>
                  <div className="text-center mb-3">
                    <div className="d-flex justify-content-center mb-3">
                      <Form.Label>Frame vertical</Form.Label>
                    </div>
                  </div>
                  <Form.Control
                    type="hidden"
                    name="json.apresentation.frame"
                    value={values.json.apresentation.frame}
                    onChange={handleChange}
                    isInvalid={!!errors?.json?.apresentation?.frame}
                    isValid={
                      touched?.json?.apresentation?.frame &&
                      !errors?.json?.apresentation?.frame
                    }
                  />
                  <div className="imagesUpload">
                    <img
                      className={`h-100 img-thumbnail ${!!errors?.json?.apresentation?.frame ? "is-invalid" : ""}`}
                      src={
                        values.json.apresentation.frame !== "" &&
                          values.json.apresentation.frame !== null
                          ? domainConfig.imageServer +
                          "/customizacao/" +
                          values.customizacao_id +
                          "/" +
                          values.json.apresentation.frame
                          : domainConfig.imageServer + "/empty-background.png"
                      }
                      alt="ImagemBase"
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {errors?.json?.apresentation?.frame}
                    </Form.Control.Feedback>
                  </div>
                  <DropzoneComponent
                    className="mt-3 dropzoneComp"
                    type="image"
                    handlePreviewSuccess={handlePreviewSuccess}
                    handlePreviewError={handlePreviewError}
                    setFieldValue={setFieldValue}
                    folder="customizacao"
                    id={values.customizacao_id}
                    field="json.apresentation.frame"
                  />
                </Card.Body>
              </Card>
            </Form.Group>
            <Form.Group as={Col} md="6">
              <Card className=" mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-center mb-3">
                    <Form.Label>Frame horizontal</Form.Label>
                  </div>
                  <Form.Control
                    type="hidden"
                    name="json.apresentation.frameHorizontal"
                    value={values.json.apresentation?.frameHorizontal}
                    onChange={handleChange}
                    isInvalid={!!errors?.json?.apresentation?.frameHorizontal}
                    isValid={
                      touched?.json?.apresentation?.frameHorizontal &&
                      !errors?.json?.apresentation?.frameHorizontal
                    }
                  />
                  <div className="imagesUpload">
                    <img
                      className={`h-100 img-thumbnail ${!!errors?.json?.apresentation?.frameHorizontal ? "is-invalid" : ""}`}
                      src={
                        values.json.apresentation?.frameHorizontal !== "" &&
                          values.json.apresentation?.frameHorizontal !== null
                          ? domainConfig.imageServer +
                          "/customizacao/" +
                          values.customizacao_id +
                          "/" +
                          values.json.apresentation.frameHorizontal
                          : domainConfig.imageServer + "/empty-background.png"
                      }
                      alt="ImagemBase"
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {errors?.json?.apresentation?.frameHorizontal}
                    </Form.Control.Feedback>
                  </div>
                  <DropzoneComponent
                    className="mt-3 dropzoneComp"
                    type="image"
                    handlePreviewSuccess={handlePreviewSuccess}
                    handlePreviewError={handlePreviewError}
                    setFieldValue={setFieldValue}
                    folder="customizacao"
                    id={values.customizacao_id}
                    field="json.apresentation.frameHorizontal"
                  />
                </Card.Body>
              </Card>
            </Form.Group>
          </Row>
        </>
      )}
      <Row className="mx-0 mb-3">
        <p className="text-start fw-bold mb-2">Tela inicial</p>
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Check
            checked={values.json.apresentation?.startPage?.show !== false} // Default true
            type="checkbox"
            label="Exibir tela inicial"
            onChange={(e) => {
              setFieldValue(
                "json.apresentation.startPage.show",
                e.target.checked,
              );
            }}
          />
        </Form.Group>
        {values.json.apresentation?.startPage?.show !== false && (
          <>
            <Form.Group className="mb-3" as={Col} sm="12">
              <Form.Label>Título</Form.Label>
              <Form.Control
                as={"textarea"}
                name="json.apresentation.startPage.title"
                value={values.json.apresentation?.startPage?.title || ""}
                onChange={handleChange}
                isInvalid={!!errors?.json?.apresentation?.startPage?.title}
                isValid={
                  touched?.json?.apresentation?.startPage?.title &&
                  !errors.json?.apresentation?.startPage?.title
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors?.json?.apresentation?.startPage?.title}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" as={Col} sm="8">
              <Form.Label>Texto do botão</Form.Label>
              <Form.Control
                type="text"
                name="json.apresentation.startPage.buttonText"
                value={values.json.apresentation?.startPage?.buttonText || ""}
                onChange={handleChange}
                isInvalid={!!errors?.json?.apresentation?.startPage?.buttonText}
                isValid={
                  touched?.json?.apresentation?.startPage?.buttonText &&
                  !errors.json?.apresentation?.startPage?.buttonText
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors?.json?.apresentation?.startPage?.buttonText}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} sm={12} className="mb-3">
              <Form.Check
                checked={
                  values.json.apresentation?.startPage?.showDemoVideo === true
                    ? true
                    : false
                }
                type="checkbox"
                name="json.apresentation.startPage.showDemoVideo"
                label="Mostrar vídeo de demonstração"
                onChange={(e) => {
                  setFieldValue(
                    "json.apresentation.startPage.showDemoVideo",
                    e.target.checked,
                  );
                }}
              />
            </Form.Group>
          </>
        )}
      </Row>

      {values.json.apresentation.configs.falai.status && (
        <Row className="mx-0 mb-3">
          <p className="text-start fw-bold mb-2">
            <FontAwesomeIcon icon={faGlobe} /> Tela de seleção de referência
            (Fal.ai)
          </p>
          <Form.Group as={Col} sm={4} className="mb-3">
            <Form.Label>Posição do container (%)</Form.Label>
            <Form.Control
              type="number"
              name="json.apresentation.configs.falai.containerPosition"
              value={values.json.apresentation.configs.falai.containerPosition}
              onChange={(e) => {
                handleChange(e);
              }}
              isInvalid={
                !!errors.json?.apresentation?.configs?.falai?.containerPosition
              }
            />
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors.json?.apresentation?.configs?.falai?.containerPosition}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} sm={4} className="mb-3">
            <Form.Label>Largura das imagens (px)</Form.Label>
            <Form.Control
              type="number"
              name="json.apresentation.configs.falai.referenceWidth"
              value={
                values.json.apresentation.configs.falai?.referenceWidth || ""
              }
              onChange={(e) => {
                handleChange(e);
              }}
              isInvalid={
                !!errors.json?.apresentation?.configs?.falai?.referenceWidth
              }
            />
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors.json?.apresentation?.configs?.falai?.referenceWidth}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
      )}

      <Row className="mx-0 mb-3">
        <p className="text-start fw-bold mb-2">Tela da câmera</p>
        <Form.Group as={Col} sm={8} className="mb-3">
          <Form.Label>Texto do botão</Form.Label>
          <Form.Control
            type="text"
            name="json.apresentation.firstPage.buttonText"
            value={values.json.apresentation.firstPage.buttonText}
            onChange={handleChange}
            isInvalid={!!errors?.json?.apresentation?.firstPage?.buttonText}
            isValid={
              touched?.json?.apresentation?.firstPage?.buttonText &&
              !errors.json?.apresentation?.firstPage?.buttonText
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors?.json?.apresentation?.firstPage?.buttonText}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} sm={4} className="mb-3">
          <Form.Label>Posição do container (%)</Form.Label>
          <Form.Control
            type="number"
            name="json.apresentation.configs.buttonPosition"
            value={values.json.apresentation.configs.buttonPosition}
            onChange={(e) => {
              handleChange(e);
            }}
            isInvalid={!!errors.json?.apresentation?.configs?.buttonPosition}
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.apresentation?.configs?.buttonPosition}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mx-0 mb-3">
        <p className="text-start fw-bold mb-2">Tela do formulário</p>
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Label>Título</Form.Label>
          <Form.Control
            as={"textarea"}
            name="json.apresentation.register.title"
            value={values.json?.apresentation?.register?.title}
            onChange={handleChange}
            isInvalid={!!errors?.json?.apresentation?.register?.title}
            isValid={
              touched?.json?.apresentation?.register?.title &&
              !errors.json?.apresentation?.register?.title
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors?.json?.apresentation?.register?.title}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mx-0 mb-3">
        <p className="text-start fw-bold mb-2">Tela do resultado</p>
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Label>Título</Form.Label>
          <Form.Control
            as={"textarea"}
            name="json.apresentation.endPage.title"
            value={values.json.apresentation.endPage.title}
            onChange={handleChange}
            isInvalid={!!errors?.json?.apresentation?.endPage?.title}
            isValid={
              touched?.json?.apresentation?.endPage?.title &&
              !errors.json?.apresentation?.endPage?.title
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors?.json?.apresentation?.endPage?.buttonText}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Label>Descrição</Form.Label>
          <Form.Control
            as={"textarea"}
            name="json.apresentation.endPage.description"
            value={values.json.apresentation.endPage.description}
            onChange={handleChange}
            isInvalid={!!errors?.json?.apresentation?.endPage?.description}
            isValid={
              touched?.json?.apresentation?.endPage?.description &&
              !errors.json?.apresentation?.endPage?.description
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors?.json?.apresentation?.endPage?.description}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3" as={Col} sm="8">
          <Form.Label>Texto do botão</Form.Label>
          <Form.Control
            type="text"
            name="json.apresentation.endPage.buttonText"
            value={values.json.apresentation.endPage.buttonText}
            onChange={handleChange}
            isInvalid={!!errors?.json?.apresentation?.endPage?.buttonText}
            isValid={
              touched?.json?.apresentation?.endPage?.buttonText &&
              !errors.json?.apresentation?.endPage?.buttonText
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors?.json?.apresentation?.endPage?.buttonText}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} sm={4} className="mb-3">
          <Form.Label>Posição do container (%)</Form.Label>
          <Form.Control
            type="number"
            name="json.apresentation.configs.buttonPositionEnd"
            value={values.json.apresentation.configs.buttonPositionEnd}
            onChange={(e) => {
              handleChange(e);
            }}
            isInvalid={!!errors.json?.apresentation?.configs?.buttonPositionEnd}
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.apresentation?.configs?.buttonPositionEnd}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} sm={6} className="mb-3">
          <Form.Label>Finalizar fluxo automaticamente (milissegundos)</Form.Label>
          <Form.Control
            type="number"
            step={1000}
            name="json.apresentation.configs.qrcodeEndPage"
            value={values.json.apresentation.configs.qrcodeEndPage}
            onChange={(e) => {
              handleChange(e);
            }}
            isInvalid={!!errors.json?.apresentation?.configs?.qrcodeEndPage}
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.apresentation?.configs?.qrcodeEndPage}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} sm={12} className="mb-3">
          <Form.Check
            checked={values.json.apresentation?.endPage?.showPrintedImage === true}
            type="checkbox"
            name="json.apresentation.endPage.showPrintedImage"
            label="Mostrar mídia capturada na tela final"
            onChange={(e) => {
              setFieldValue(
                "json.apresentation.endPage.showPrintedImage",
                e.target.checked,
              );
              if (e.target.checked) {
                setFieldValue(
                  "json.apresentation.endPage.showPrintedImageResult",
                  false,
                );
              }
            }}
          />
          <Form.Check
            checked={values.json.apresentation?.endPage?.showPrintedImageResult === true}
            type="checkbox"
            name="json.apresentation.endPage.showPrintedImageResult"
            label="Mostrar resultado processado na tela final"
            onChange={(e) => {
              setFieldValue(
                "json.apresentation.endPage.showPrintedImageResult",
                e.target.checked,
              );
              if (e.target.checked) {
                setFieldValue(
                  "json.apresentation.endPage.showPrintedImage",
                  false,
                );
                setFieldValue(
                  "json.apresentation.configs.qrcodeEndPage",
                  120000,
                );
              }
            }}
          />
        </Form.Group>
      </Row>
    </>
  );
};

const GalleryPresentation = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
}) => {
  return (
    <Row className="mx-0 mb-3">
      <p className="text-start fw-bold mb-2">Galeria</p>
      <Form.Group as={Col} sm={6} className="mb-3">
        <Form.Label>Tempo entre slides (milissegundos)</Form.Label>
        <Form.Control
          type="number"
          step={1000}
          name="json.apresentation.configs.galleryTransition"
          value={values.json.apresentation.configs.galleryTransition}
          onChange={(e) => {
            handleChange(e);
          }}
          isInvalid={!!errors.json?.apresentation?.configs?.galleryTransition}
        />
        <Form.Control.Feedback type="valid"></Form.Control.Feedback>
        <Form.Control.Feedback type="invalid">
          {errors.json?.apresentation?.configs?.galleryTransition}
        </Form.Control.Feedback>
      </Form.Group>
    </Row>
  );
};

const ImageLibrary = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
}) => {
  return (
    <Row className="mx-0 mb-3">
      <p className="my-5 text-center opacity-75">
        Nenhuma configuração disponível
      </p>
    </Row>
  );
};

export default Photobooth;
